import React, { useState, useEffect, useCallback } from 'react';
import './AdminPage.css';
import {
  getPendingPriceReviews, approvePriceReview, rejectPriceReview,
  batchApprovePriceReviews, getLatestFetchRun, getSystemConfig,
  getAllProducts, deleteProduct, getActiveUsers, db,
  getAliases, deleteAlias, saveAliasDirect, saveIgnoreAlias, saveBacklogAlias, getBacklogItems,
  getUnmatchedReceiptItems, getRecentMatchedItems,
  createProductDirect, getDashboardMetrics, getTestUserIds, toggleTestUser,
} from '../utils/db';
import { collection, getDocs, orderBy, query, limit, updateDoc, writeBatch, doc as firestoreDoc } from 'firebase/firestore';
import ProductDataAgent from '../components/agent/ProductDataAgent';
import ProductRequests from '../components/requests/ProductRequests';
import { getPendingCount } from '../components/requests/requestsHelpers';
import { auth } from '../firebase';
import { AISLES, SUBCATEGORY_CONFIG } from '../utils/taxonomy';
import { storeIsSupported, getStoreName } from '../utils/storeConfig';

const STORE_NAMES = {
  tesco:      'Tesco',
  asda:       'Asda',
  sainsburys: "Sainsbury's",
  aldi:       'Aldi',
  lidl:       'Lidl',
  morrisons:  'Morrisons',
  waitrose:   'Waitrose',
};

function storeName(id) {
  if (!id) return '—';
  return STORE_NAMES[(id || '').replace(/^store_/, '')] || id;
}

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    + ' · '
    + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

// ── Two-level nav structure ───────────────────────────────────────────────────
// Top-level sections → sub-tabs within each section
const SECTIONS = {
  dashboard: { label: 'Dashboard', tabs: ['dashboard'] },
  queue:     { label: 'Queue',    tabs: ['reviews', 'requests', 'receipts'] },
  products:  { label: 'Products', tabs: ['products'] },
  feedback:  { label: 'Feedback', tabs: ['feedback'] },
  tools:     { label: 'Tools',    tabs: ['agent', 'aliases'] },
};

// Default sub-tab when entering each section
const SECTION_DEFAULT = { dashboard: 'dashboard', queue: 'reviews', products: 'products', feedback: 'feedback', tools: 'agent' };

const TAB_LABELS = {
  dashboard: 'Dashboard',
  reviews:   'Price Reviews',
  requests:  'Requests',
  receipts:  'Receipts',
  products:  'Products',
  feedback:  'Feedback',
  agent:     'Product Agent',
  aliases:   'Aliases',
};

const TAB_SUBTITLES = {
  reviews:  'Review and approve pending price changes',
  agent:    'Validate & stage product data · Rules v1.4',
  requests: 'Missing product demand signals',
  products: 'View and delete products from the catalogue',
  receipts: 'Unmatched items from receipt scans',
  feedback: 'User-submitted bugs, ideas and thoughts',
};

const FEEDBACK_TYPE_LABELS = {
  bug:          '🐛 Bug',
  idea:         '💡 Idea',
  general:      '💬 Thoughts',
  // Beta / in-app feedback types
  scan_quality:  '📷 Scan quality',
  return_visit:  '🔁 Return visit',
  return_intent: '🎯 Return intent',
  beta_stepper:  '📋 Beta survey',
};
const FEEDBACK_STATUS_LABELS = { new: 'New', reviewed: 'Reviewed', actioned: 'Actioned', archived: 'Archived' };
const FEEDBACK_STATUS_COLORS = { new: '#dc2626', reviewed: '#d97706', actioned: '#16a34a', archived: '#9ca3af' };
const FEEDBACK_PRIORITY_LABELS = { high: '🔴 High', medium: '🟡 Medium', low: '⚪ Low' };

// Human-readable labels for response keys / values
const RESPONSE_KEY_LABELS = {
  answer:          'Rating',
  followUpText:    'Details',
  reason:          'Reason',
  intent:          'Intent',
  scanQuality:     'Scan quality',
  scanQualityText: 'Scan quality details',
  clarity:         'Clarity',
  clarityText:     'Clarity details',
  returnIntent:    'Would return?',
  openText:        'Open feedback',
};
const RESPONSE_VALUE_LABELS = {
  yes_mostly:    '👍 Yes, mostly right',
  some_issues:   '😐 Some issues',
  not_really:    '👎 Not really',
  checking_list: 'Checking list',
  price_check:   'Price check',
  yes:           'Yes',
  no:            'No',
  maybe:         'Maybe',
};

function formatFeedbackContent(item) {
  // Standard feedback form — has a message field
  if (item.message) return item.message;

  // Beta / structured feedback — render responses object
  if (item.responses && typeof item.responses === 'object') {
    return Object.entries(item.responses)
      .filter(([, v]) => v != null && v !== '')
      .map(([k, v]) => {
        const label = RESPONSE_KEY_LABELS[k] || k;
        const value = RESPONSE_VALUE_LABELS[v] || v;
        return `${label}: ${value}`;
      })
      .join('\n');
  }

  return null;
}

const SOURCE_LABELS = {
  community_manual: 'Community',
  system_fetch:     'Fetcher',
  receipt_ocr:      'Receipt',
};

function formatPence(pence) {
  if (pence == null) return '—';
  return `£${(pence / 100).toFixed(2)}`;
}

function diffLabel(proposed, current) {
  if (proposed == null || current == null) return null;
  const diff = proposed - current;
  if (diff === 0) return null;
  const sign   = diff > 0 ? '+' : '';
  const pence  = Math.abs(diff);
  const str    = pence >= 100 ? `${sign}£${(diff / 100).toFixed(2)}` : `${sign}${diff}p`;
  const pct    = ((diff / current) * 100).toFixed(1);
  return { str: `${str} (${pct}%)`, up: diff > 0 };
}

function timeAgo(ts) {
  if (!ts) return '—';
  const ms   = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  const secs = Math.floor((Date.now() - ms) / 1000);
  if (secs < 60)   return 'just now';
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400)return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

// ── Add-to-product helpers ────────────────────────────────────────────────────

// Pull size out of receipt text e.g. "Olivio Oil 500ml" → "500ml"
function parseSize(text) {
  const m = text.match(/\b(\d+(?:\.\d+)?\s*(?:ml|l|ltr|g|kg|oz|lb|cl|pack|pk|ct|pcs|sheets|rolls|tabs|wipes|bag|bags))\b/i);
  return m ? m[1].trim() : '';
}

// Guess brand: first word if it looks capitalised and isn't a common generic
const GENERIC_STARTS = new Set(['organic', 'free', 'fresh', 'whole', 'extra', 'big', 'mini', 'new', 'old', 'light', 'low', 'no', 'with']);
function parseBrand(text) {
  const first = text.split(/\s+/)[0] || '';
  if (!first || GENERIC_STARTS.has(first.toLowerCase())) return '';
  // If it looks like a proper noun (starts uppercase in original) it's probably a brand
  return /^[A-Z]/.test(text.split(/\s+/)[0]) ? first : '';
}

// Build the 26-column tab-separated sheet row (matches sync-sheet.js column order)
function buildSheetRow({ productId, productName, category, subcategory, standardizedSize, unitType, brand, ownBrand, storeProductName, variant, storageType, storeSize, store, price }) {
  const today = new Date().toISOString().slice(0, 10);
  const cols = Array(26).fill('');
  cols[0]  = productId;                           // productId (family slug)
  cols[1]  = productName;                         // Product_name_app
  cols[2]  = category;                            // category
  cols[3]  = subcategory;                         // subcategory
  cols[4]  = standardizedSize;                    // standardizedSize
  cols[5]  = unitType;                            // unitType
  cols[6]  = brand || '';                         // brand
  cols[7]  = storeProductName;                    // Product_name_store
  cols[8]  = variant;                             // variant
  cols[9]  = storageType;                         // storageType: chilled | ambient | frozen
  cols[10] = storeSize;                           // storeSize (number only)
  cols[11] = '';                                  // barcode
  cols[12] = '';                                  // storeProductId
  cols[13] = ownBrand ? 'true' : 'false';         // OwnBrand
  cols[14] = 'standard';                          // tier
  cols[15] = store;                               // Store
  cols[16] = price;                               // Price
  cols[24] = today;                               // Date_checked
  cols[25] = 'low';                               // confidence
  return cols.join('\t');
}

// Category dropdown — one entry per aisle, using the canonical aisle id
const ALL_CATEGORIES = AISLES.map(a => ({ id: a.id, name: a.name }));
const ALL_SUBCATEGORIES = Object.entries(SUBCATEGORY_CONFIG).map(([id, cfg]) => ({ id, name: cfg.name }));

const STORE_DISPLAY_NAMES = {
  store_tesco:       'Tesco',
  store_sainsburys:  "Sainsbury's",
  store_aldi:        'Aldi',
  store_lidl:        'Lidl',
  store_asda:        'Asda',
  store_morrisons:   'Morrisons',
  store_waitrose:    'Waitrose',
};

// ─────────────────────────────────────────────────────────────────────────────

function AdminPage({ onBack }) {
  const [adminSection, setAdminSection] = useState('dashboard');
  const [adminTab,     setAdminTab]     = useState('dashboard');

  // Navigate to a section — sets section + resets to its default sub-tab
  const goToSection = (section) => {
    setAdminSection(section);
    setAdminTab(SECTION_DEFAULT[section]);
  };

  // ── Price reviews state ───────────────────────────────────────
  const [reviews,      setReviews]      = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [reviewFilter, setReviewFilter] = useState('pending');
  const [actioning,    setActioning]    = useState(new Set()); // reviewIds being actioned
  const [batchConfirm, setBatchConfirm] = useState(false);
  const [batching,     setBatching]     = useState(false);
  const [lastRun,      setLastRun]      = useState(null);
  const [threshold,    setThreshold]    = useState(0.15);

  // ── Shared state ──────────────────────────────────────────────
  const [requestsBadge, setRequestsBadge] = useState(0);

  // ── Product management state ──────────────────────────────────
  const [products,        setProducts]        = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productSearch,   setProductSearch]   = useState('');
  const [confirmDelete,   setConfirmDelete]   = useState(null);
  const [deleting,        setDeleting]        = useState(null);
  const [expandedProduct, setExpandedProduct] = useState(null);

  // ── Receipts tab state ────────────────────────────────────────
  const [receiptsList,      setReceiptsList]      = useState([]);
  const [receiptsLoading,   setReceiptsLoading]   = useState(false);
  const [receiptsError,     setReceiptsError]     = useState(null);
  const [receiptsFilter,    setReceiptsFilter]    = useState('new');     // 'new' | 'reviewed'
  const [reviewingReceipt,  setReviewingReceipt]  = useState(null);      // id being toggled
  const [expandedGroup,     setExpandedGroup]     = useState(null);      // groupKey currently open
  const [markingGroup,      setMarkingGroup]      = useState(null);      // groupKey being batch-marked

  // ── Presence / active users state ───────────────────────────
  const [activeUsers,        setActiveUsers]        = useState([]);
  const [presenceLoading,    setPresenceLoading]    = useState(false);
  const [presenceLastFetched, setPresenceLastFetched] = useState(null);
  const [testUserIds,        setTestUserIds]        = useState([]);

  const fetchPresence = useCallback(async () => {
    setPresenceLoading(true);
    try {
      const [users, tids] = await Promise.all([
        getActiveUsers(5),
        getTestUserIds(),
      ]);
      setActiveUsers(users);
      setTestUserIds(tids);
      setPresenceLastFetched(new Date());
    } finally {
      setPresenceLoading(false);
    }
  }, []);

  // ── Dashboard metrics state ───────────────────────────────────
  const [dashboardMetrics,  setDashboardMetrics]  = useState(null);
  const [dashboardLoading,  setDashboardLoading]  = useState(false);
  const [dashboardWindow,   setDashboardWindow]   = useState(7); // days

  const loadDashboard = useCallback(async (days) => {
    setDashboardLoading(true);
    try {
      const [metrics] = await Promise.all([
        getDashboardMetrics(days),
        fetchPresence(),
      ]);
      setDashboardMetrics(metrics);
    } finally {
      setDashboardLoading(false);
    }
  }, [fetchPresence]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleTestUser = useCallback(async (uid) => {
    const updated = await toggleTestUser(uid);
    setTestUserIds(updated);
    // Reload metrics so they immediately reflect the change
    setDashboardMetrics(null);
    loadDashboard(dashboardWindow);
  }, [loadDashboard, dashboardWindow]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (adminSection === 'dashboard') loadDashboard(dashboardWindow);
  }, [adminSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Feedback tab state ────────────────────────────────────────
  const [feedbackItems,        setFeedbackItems]        = useState([]);
  const [feedbackLoading,      setFeedbackLoading]      = useState(false);
  const [feedbackError,        setFeedbackError]        = useState(null);
  const [feedbackStatusFilter, setFeedbackStatusFilter] = useState(null); // null=active(new+reviewed) | 'new'|'reviewed'|'actioned'|'archived'|'all'
  const [feedbackTypeFilter,   setFeedbackTypeFilter]   = useState('all');    // 'all'|'bug'|'idea'|'general'
  const [feedbackPrioFilter,   setFeedbackPrioFilter]   = useState('all');    // 'all'|'high'|'medium'|'low'
  const [expandedFeedback,     setExpandedFeedback]     = useState(null);
  const [savingNote,           setSavingNote]           = useState(null);
  const [noteText,        setNoteText]        = useState('');

  // ── Add-to-product modal state ────────────────────────────────
  const [addProductModal, setAddProductModal] = useState(null); // null | { receiptText, storeId }
  const [apForm, setApForm] = useState({});   // controlled form fields
  const [apCopied, setApCopied] = useState(false);

  // ── Aliases tab state ─────────────────────────────────────────
  const [aliasMode,         setAliasMode]         = useState('review'); // 'review'|'unmatched'|'aliases'
  const [aliases,           setAliases]           = useState([]);
  const [aliasesLoading,    setAliasesLoading]    = useState(false);
  const [aliasSearch,       setAliasSearch]       = useState('');
  const [aliasSourceFilter, setAliasSourceFilter] = useState('all');
  const [deletingAlias,     setDeletingAlias]     = useState(null);
  const [unmatchedItems,    setUnmatchedItems]    = useState([]);
  const [unmatchedLoading,  setUnmatchedLoading]  = useState(false);
  const [unmatchedActions,  setUnmatchedActions]  = useState({}); // { [key]: 'ignoring'|'matching'|'done'|'ignored'|'matched' }
  const [matchingItem,      setMatchingItem]      = useState(null); // item being matched
  const [matchSearch,       setMatchSearch]       = useState('');
  const [matchResults,      setMatchResults]      = useState([]);
  const [matchSearchLoading,setMatchSearchLoading]= useState(false);
  const [allProducts,       setAllProducts]       = useState(null);
  // Review mode state
  const [reviewItems,       setReviewItems]       = useState([]);
  const [reviewLoading,     setReviewLoading]     = useState(false);
  const [reviewActions,     setReviewActions]     = useState({}); // { [key]: 'confirming'|'confirmed'|'fixing'|'fixed' }
  const [reviewFixItem,     setReviewFixItem]     = useState(null);
  const [reviewFixSearch,   setReviewFixSearch]   = useState('');
  const [reviewFixResults,  setReviewFixResults]  = useState([]);
  const [reviewFixLoading,  setReviewFixLoading]  = useState(false);
  // Create-new-product panel (shared between Review and Unmatched)
  const [createPanel,       setCreatePanel]       = useState(null); // null | { item, sourceType, form, idEdited }
  const [createSaving,      setCreateSaving]      = useState(false);
  const [createError,       setCreateError]       = useState(null);
  const [createdCopy,       setCreatedCopy]       = useState(null); // null | { productId, name, ... } — post-creation copy prompt
  const [copyConfirmed,     setCopyConfirmed]     = useState(false);
  // Unmatched sub-mode
  const [unmatchedMode,     setUnmatchedMode]     = useState('queue'); // 'queue' | 'backlog'
  const [backlogItems,      setBacklogItems]      = useState([]);
  const [backlogLoading,    setBacklogLoading]    = useState(false);
  const [backlogActions,    setBacklogActions]    = useState({}); // { [alias]: 'moving'|'moved'|'ignoring'|'ignored' }

  useEffect(() => { setRequestsBadge(getPendingCount()); }, [adminTab]);

  const loadReviews = useCallback(async () => {
    setReviewsLoading(true);
    const [data, run, config] = await Promise.all([
      getPendingPriceReviews(),
      getLatestFetchRun(),
      getSystemConfig(),
    ]);
    setReviews(data);
    setLastRun(run);
    setThreshold(config.batchApproveThreshold ?? 0.15);
    setReviewsLoading(false);
  }, []);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  useEffect(() => {
    if (adminTab === 'products' && products.length === 0) {
      setProductsLoading(true);
      getAllProducts().then(data => {
        setProducts(data.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
        setProductsLoading(false);
      });
    }
  }, [adminTab]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (adminTab !== 'aliases') return;
    setAliasesLoading(true);
    setUnmatchedLoading(true);
    setReviewLoading(true);
    getAliases(db).then(data => {
      setAliases(data);
      setAliasesLoading(false);
    }).catch(() => setAliasesLoading(false));
    getUnmatchedReceiptItems(db, 20).then(data => {
      setUnmatchedItems(data);
      setUnmatchedLoading(false);
    }).catch(() => setUnmatchedLoading(false));
    getRecentMatchedItems(db, 20).then(data => {
      setReviewItems(data);
      setReviewLoading(false);
    }).catch(() => setReviewLoading(false));
    setBacklogLoading(true);
    getBacklogItems(db).then(data => {
      setBacklogItems(data);
      setBacklogLoading(false);
    }).catch(() => setBacklogLoading(false));
  }, [adminTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeleteAlias = async (aliasId) => {
    if (!window.confirm('Delete this alias? This cannot be undone.')) return;
    setDeletingAlias(aliasId);
    await deleteAlias(db, aliasId);
    setAliases(prev => prev.filter(a => a.id !== aliasId));
    setDeletingAlias(null);
  };

  const handleIgnoreUnmatched = async (item) => {
    const key = `${item.productText}|${item.storeId || ''}`;
    setUnmatchedActions(prev => ({ ...prev, [key]: 'ignoring' }));
    try {
      const currentUser = auth.currentUser;
      await saveIgnoreAlias(db, { receiptText: item.productText, storeId: item.storeId || '', uid: currentUser?.uid });
      setUnmatchedActions(prev => ({ ...prev, [key]: 'ignored' }));
    } catch (err) {
      console.error('handleIgnoreUnmatched:', err);
      setUnmatchedActions(prev => ({ ...prev, [key]: null }));
    }
  };

  const handleBacklogUnmatched = async (item) => {
    const key = `${item.productText}|${item.storeId || ''}`;
    setUnmatchedActions(prev => ({ ...prev, [key]: 'backlogs' }));
    try {
      const currentUser = auth.currentUser;
      await saveBacklogAlias(db, { receiptText: item.productText, storeId: item.storeId || '', uid: currentUser?.uid, count: item.count || 1 });
      setUnmatchedActions(prev => ({ ...prev, [key]: 'backlogged' }));
      setBacklogItems(prev => {
        // Add to local backlog state (grouped by alias text)
        const normText = item.productText.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
        const existing = prev.find(b => b.productText === normText);
        if (existing) {
          return prev.map(b => b.productText === normText
            ? { ...b, stores: [...b.stores.filter(s => s.storeId !== (item.storeId || '')), { storeId: item.storeId || '', count: item.count || 1, docId: null }], totalCount: b.totalCount + (item.count || 1) }
            : b
          );
        }
        return [{ productText: normText, stores: [{ storeId: item.storeId || '', count: item.count || 1, docId: null }], totalCount: item.count || 1 }, ...prev];
      });
    } catch (err) {
      console.error('handleBacklogUnmatched:', err);
      setUnmatchedActions(prev => ({ ...prev, [key]: null }));
    }
  };

  // Move a backlog group back to the queue (delete all alias docs for this text)
  const handleMoveBacklogToQueue = async (group) => {
    setBacklogActions(prev => ({ ...prev, [group.productText]: 'moving' }));
    try {
      await Promise.all(group.stores.filter(s => s.docId).map(s => deleteAlias(db, s.docId)));
      setBacklogActions(prev => ({ ...prev, [group.productText]: 'moved' }));
      setBacklogItems(prev => prev.filter(b => b.productText !== group.productText));
      // Reload unmatched queue so it reappears
      getUnmatchedReceiptItems(db, 20).then(data => setUnmatchedItems(data));
    } catch (err) {
      console.error('handleMoveBacklogToQueue:', err);
      setBacklogActions(prev => ({ ...prev, [group.productText]: null }));
    }
  };

  // Permanently ignore a backlog item
  const handleIgnoreBacklog = async (group) => {
    setBacklogActions(prev => ({ ...prev, [group.productText]: 'ignoring' }));
    try {
      const currentUser = auth.currentUser;
      await Promise.all(group.stores.map(s =>
        saveIgnoreAlias(db, { receiptText: group.productText, storeId: s.storeId, uid: currentUser?.uid })
      ));
      setBacklogActions(prev => ({ ...prev, [group.productText]: 'ignored' }));
      setBacklogItems(prev => prev.filter(b => b.productText !== group.productText));
    } catch (err) {
      console.error('handleIgnoreBacklog:', err);
      setBacklogActions(prev => ({ ...prev, [group.productText]: null }));
    }
  };

  const loadProductsForMatch = async () => {
    if (allProducts) return allProducts;
    const snap = await getDocs(query(collection(db, 'products'), limit(500)));
    const prods = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(p => p.active !== false);
    setAllProducts(prods);
    return prods;
  };

  const handleMatchSearch = async (q) => {
    setMatchSearch(q);
    if (!q.trim()) { setMatchResults([]); return; }
    setMatchSearchLoading(true);
    const prods = await loadProductsForMatch();
    const lower = q.toLowerCase();
    setMatchResults(prods.filter(p => p.name?.toLowerCase().includes(lower) || p.id.toLowerCase().includes(lower)).slice(0, 8));
    setMatchSearchLoading(false);
  };

  const handleSaveMatch = async (item, product) => {
    const key = `${item.productText}|${item.storeId || ''}`;
    setUnmatchedActions(prev => ({ ...prev, [key]: 'matching' }));
    try {
      const currentUser = auth.currentUser;
      await saveAliasDirect(db, {
        receiptText: item.productText,
        productId:   product.id,
        productName: product.name,
        storeId:     item.storeId || '',
        uid:         currentUser?.uid,
      });
      setUnmatchedActions(prev => ({ ...prev, [key]: 'matched' }));
      setMatchingItem(null);
      setMatchSearch('');
      setMatchResults([]);
    } catch (err) {
      console.error('handleSaveMatch:', err);
      setUnmatchedActions(prev => ({ ...prev, [key]: null }));
    }
  };

  // ── Review mode handlers ──────────────────────────────────────

  const handleIgnoreReview = async (item) => {
    const key = `${item.productText}|${item.storeId || ''}`;
    setReviewActions(prev => ({ ...prev, [key]: 'ignoring' }));
    try {
      const currentUser = auth.currentUser;
      await saveIgnoreAlias(db, { receiptText: item.productText, storeId: item.storeId || '', uid: currentUser?.uid });
      setReviewActions(prev => ({ ...prev, [key]: 'ignored' }));
    } catch (err) {
      console.error('handleIgnoreReview:', err);
      setReviewActions(prev => ({ ...prev, [key]: null }));
    }
  };

  const handleReviewConfirm = async (item) => {
    const key = `${item.productText}|${item.storeId || ''}`;
    setReviewActions(prev => ({ ...prev, [key]: 'confirming' }));
    try {
      const currentUser = auth.currentUser;
      await saveAliasDirect(db, {
        receiptText: item.productText,
        productId:   item.matchedProductId,
        productName: item.matchedProductName,
        storeId:     item.storeId || '',
        uid:         currentUser?.uid,
        source:      'human_verified',
      });
      setReviewActions(prev => ({ ...prev, [key]: 'confirmed' }));
    } catch (err) {
      console.error('handleReviewConfirm:', err);
      setReviewActions(prev => ({ ...prev, [key]: null }));
    }
  };

  const handleReviewFixSearch = async (q) => {
    setReviewFixSearch(q);
    if (!q.trim()) { setReviewFixResults([]); return; }
    setReviewFixLoading(true);
    const prods = await loadProductsForMatch();
    const lower = q.toLowerCase();
    setReviewFixResults(
      prods.filter(p => p.name?.toLowerCase().includes(lower) || p.id.toLowerCase().includes(lower)).slice(0, 8)
    );
    setReviewFixLoading(false);
  };

  const handleSaveReviewFix = async (item, product) => {
    const key = `${item.productText}|${item.storeId || ''}`;
    setReviewActions(prev => ({ ...prev, [key]: 'fixing' }));
    try {
      const currentUser = auth.currentUser;
      await saveAliasDirect(db, {
        receiptText:       item.productText,
        productId:         product.id,
        productName:       product.name,
        storeId:           item.storeId || '',
        uid:               currentUser?.uid,
        source:            'user_correction',
        originalProductId: item.matchedProductId,
      });
      setReviewActions(prev => ({ ...prev, [key]: 'fixed' }));
      setReviewFixItem(null);
      setReviewFixSearch('');
      setReviewFixResults([]);
    } catch (err) {
      console.error('handleSaveReviewFix:', err);
      setReviewActions(prev => ({ ...prev, [key]: null }));
    }
  };

  // ── Create-new-product helpers ────────────────────────────────

  // Store-name prefixes to strip from receipt text before pre-filling name
  const RECEIPT_STORE_PREFIXES = /^(tesco|sainsbury'?s?|sainsburys|asda|aldi|lidl|morrisons|waitrose|m&s|marks & spencer)\s+/i;

  function nameToProductId(name) {
    return 'prod_' + name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function findSimilarProducts(name, prods) {
    if (!name || !prods) return [];
    const words = name.toLowerCase().split(/\s+/).filter(w => w.length > 3);
    return prods.filter(p => {
      const pName = (p.name || '').toLowerCase();
      return words.some(w => pName.includes(w));
    }).slice(0, 3);
  }

  const handleOpenCreate = async (item, sourceType) => {
    const text = item.productText || item.text || '';
    const size  = parseSize(text);
    // Strip store name prefix, size suffix, trailing punctuation
    const stripped = text
      .replace(RECEIPT_STORE_PREFIXES, '')
      .replace(new RegExp(`\\s*${size}\\s*$`, 'i'), '')
      .replace(/[\s\-–—]+$/, '')
      .trim();
    const cleanName = stripped || text;
    const productId = nameToProductId(cleanName);
    await loadProductsForMatch();
    setCreatedCopy(null);
    setCreatePanel({
      item,
      sourceType,
      idEdited: false,
      form: {
        name:        cleanName,
        size,
        category:    '',
        subcategory: '',
        variant:     '',
        storageType: '',
        ownBrand:    false,
        brand:       '',
        productId,
      },
    });
    setCreateError(null);
  };

  const handleCreateFormChange = (field, value) => {
    setCreatePanel(prev => {
      if (!prev) return prev;
      const updatedForm = { ...prev.form, [field]: value };
      // Auto-sync ID from name unless admin has manually edited it
      if (field === 'name' && !prev.idEdited) {
        updatedForm.productId = nameToProductId(value);
      }
      const idEdited = field === 'productId' ? true : prev.idEdited;
      return { ...prev, idEdited, form: updatedForm };
    });
  };

  const handleCreateAndLink = async () => {
    if (!createPanel) return;
    const { item, sourceType, form } = createPanel;
    if (!form.name.trim())      { setCreateError('Product name is required.'); return; }
    if (!form.productId.trim()) { setCreateError('Product ID is required.'); return; }

    setCreateSaving(true);
    setCreateError(null);
    try {
      const productId  = form.productId.trim();
      const currentUser = auth.currentUser;

      await createProductDirect(db, {
        productId,
        name:        form.name.trim(),
        category:    form.category     || null,
        subcategory: form.subcategory  || null,
        variant:     form.variant      || null,
        storageType: form.storageType  || null,
        brand:       (!form.ownBrand && form.brand.trim()) ? form.brand.trim() : null,
        ownBrand:    form.ownBrand,
        uid:         currentUser?.uid,
      });

      await saveAliasDirect(db, {
        receiptText: item.productText,
        productId,
        productName: form.name.trim(),
        storeId:     item.storeId || '',
        uid:         currentUser?.uid,
        source:      'manual_creation',
      });

      // Update local panel state
      const key = `${item.productText}|${item.storeId || ''}`;
      if (sourceType === 'review') {
        setReviewActions(prev => ({ ...prev, [key]: 'fixed' }));
        setReviewFixItem(null);
        setReviewFixSearch('');
        setReviewFixResults([]);
      } else {
        setUnmatchedActions(prev => ({ ...prev, [key]: 'matched' }));
        setMatchingItem(null);
        setMatchSearch('');
        setMatchResults([]);
      }

      setAllProducts(prev => prev ? [...prev, { id: productId, name: form.name.trim(), category: form.category || null, active: true }] : prev);

      // Store details for the sheet-copy step, then close the create panel
      setCreatedCopy({
        productId,
        name:        form.name.trim(),
        category:    form.category    || '',
        subcategory: form.subcategory || '',
        variant:     form.variant     || '',
        storageType: form.storageType || '',
        brand:       (!form.ownBrand && form.brand.trim()) ? form.brand.trim() : '',
        ownBrand:    form.ownBrand,
        size:        form.size        || '',
        receiptText: item.productText,
        storeId:     item.storeId     || '',
      });
      setCreatePanel(null);
    } catch (err) {
      console.error('handleCreateAndLink:', err);
      setCreateError('Failed to create product. Check the ID is unique and try again.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handleCopyCreatedRow = () => {
    if (!createdCopy) return;
    const row = buildSheetRow({
      productId:        createdCopy.productId,
      productName:      createdCopy.name,
      category:         createdCopy.category,
      subcategory:      createdCopy.subcategory,
      standardizedSize: createdCopy.size,
      unitType:         '',
      brand:            createdCopy.brand,
      ownBrand:         createdCopy.ownBrand,
      storeProductName: createdCopy.receiptText,
      variant:          createdCopy.variant,
      storageType:      createdCopy.storageType,
      storeSize:        createdCopy.size.replace(/[^0-9.]/g, ''),
      store:            STORE_DISPLAY_NAMES[createdCopy.storeId] || '',
      price:            '',
    });
    navigator.clipboard.writeText(row).then(() => {
      setCopyConfirmed(true);
      setTimeout(() => setCopyConfirmed(false), 2500);
    }).catch(() => {});
  };

  // Derives a receipt-level status badge from stored fields.
  // priority 1 = most urgent (fix now), 4 = ignore.
  // action   = right-aligned workflow label shown on the row.
  const deriveReceiptStatus = (r) => {
    if (!r.detectedStoreId)
      return { emoji: '⚠️', label: 'Store not detected',       type: 'warn',  priority: 1, action: '🔧 Fix detection'       };
    if (r.status === 'failed' || (r.itemCount || 0) < 2)
      return { emoji: '❌', label: 'Parse likely failed',       type: 'error', priority: 2, action: '🧠 Improve parsing'      };
    if ((r.matchedCount || 0) === 0 && storeIsSupported(r.detectedStoreId))
      return { emoji: '🟡', label: 'Parsed, no matches',        type: 'info',  priority: 3, action: '➕ Add products'         };
    if (!storeIsSupported(r.detectedStoreId))
      return { emoji: '🟡', label: 'Expected · catalogue gap',  type: 'info',  priority: 4, action: '⏭ Ignore (coming soon)' };
    return   { emoji: '✅', label: 'Parsed & matched',           type: 'ok',    priority: 5, action: null                    };
  };

  const loadReceipts = useCallback(async () => {
    setReceiptsLoading(true);
    setReceiptsError(null);
    try {
      const q    = query(collection(db, 'receipts'), orderBy('processedAt', 'desc'), limit(50));
      const snap = await getDocs(q);
      const list = snap.docs.map(snapDoc => {
        const d = snapDoc.data();
        return {
          id:               snapDoc.id,
          detectedStoreId:  d.detectedStoreId  ?? null,
          status:           d.status           ?? 'complete',
          itemCount:        d.itemCount        ?? 0,
          matchedCount:     d.matchedCount     ?? 0,
          processedAt:      d.processedAt?.toDate?.() ?? null,
          parserMode:       d.pipeline?.parserMode     ?? null,
          fallbackReason:   d.pipeline?.layoutDebug?.fallbackReason ?? null,
          prePairTriggered: d.pipeline?.layoutDebug?.prePair?.triggered ?? null,
          reviewed:         d.reviewed         ?? false,
        };
      });
      setReceiptsList(list);
    } catch (err) {
      console.error('[AdminPage] loadReceipts error:', err);
      setReceiptsError(err.message);
    } finally {
      setReceiptsLoading(false);
    }
  }, []);

  // Groups a receipt into a key used to cluster similar problems together.
  const deriveGroupKey = (r) => {
    if (!r.detectedStoreId)                             return 'unknown_store';
    if (r.status === 'failed' || (r.itemCount || 0) < 2) return 'parse_failed';
    if ((r.matchedCount || 0) > 0)                      return 'matched';
    if (!storeIsSupported(r.detectedStoreId))           return r.detectedStoreId;          // e.g. 'store_coop'
    return `${r.detectedStoreId}_unmatched`;                                                // e.g. 'store_tesco_unmatched'
  };

  // Returns display metadata for a group key.
  const deriveGroupMeta = (groupKey) => {
    if (groupKey === 'unknown_store')
      return { emoji: '⚠️', label: 'Unknown store',       type: 'warn',  action: '🔧 Fix detection',       priority: 1 };
    if (groupKey === 'parse_failed')
      return { emoji: '❌', label: 'Parse likely failed',  type: 'error', action: '🧠 Improve parsing',     priority: 2 };
    if (groupKey === 'matched')
      return { emoji: '✅', label: 'Parsed & matched',      type: 'ok',    action: null,                    priority: 5 };
    if (groupKey.endsWith('_unmatched')) {
      const name = getStoreName(groupKey.replace('_unmatched', ''));
      return { emoji: '🟡', label: `${name} unmatched`,    type: 'info',  action: '➕ Add products',        priority: 3 };
    }
    // Coming-soon store (groupKey === storeId)
    return { emoji: '🟡', label: getStoreName(groupKey),   type: 'info',  action: '⏭ Ignore (coming soon)', priority: 4 };
  };

  const markGroupReviewed = async (groupKey, receipts, reviewed = true) => {
    setMarkingGroup(groupKey);
    try {
      const batch = writeBatch(db);
      receipts.forEach(r => batch.update(firestoreDoc(db, 'receipts', r.id), { reviewed }));
      await batch.commit();
      const ids = new Set(receipts.map(r => r.id));
      setReceiptsList(prev => prev.map(r => ids.has(r.id) ? { ...r, reviewed } : r));
    } catch (err) {
      console.error('[AdminPage] markGroupReviewed error:', err);
    } finally {
      setMarkingGroup(null);
    }
  };

  const markReceiptReviewed = async (id, reviewed = true) => {
    setReviewingReceipt(id);
    try {
      await updateDoc(firestoreDoc(db, 'receipts', id), { reviewed });
      setReceiptsList(prev => prev.map(r => r.id === id ? { ...r, reviewed } : r));
    } catch (err) {
      console.error('[AdminPage] markReceiptReviewed error:', err);
    } finally {
      setReviewingReceipt(null);
    }
  };

  useEffect(() => {
    if (adminTab === 'receipts') loadReceipts();
  }, [adminTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadFeedback = useCallback(async () => {
    setFeedbackLoading(true);
    setFeedbackError(null);
    try {
      const snap = await getDocs(
        query(collection(db, 'feedback'), orderBy('createdAt', 'desc'), limit(200))
      );
      setFeedbackItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('[AdminPage] loadFeedback error:', err);
      setFeedbackError(err.message);
    } finally {
      setFeedbackLoading(false);
    }
  }, []);

  useEffect(() => {
    if (adminTab === 'feedback') loadFeedback();
  }, [adminTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateFeedbackStatus = async (id, status) => {
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'feedback', id), { status });
    setFeedbackItems(prev => prev.map(f => f.id === id ? { ...f, status } : f));
  };

  const updateFeedbackPriority = async (id, priority) => {
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'feedback', id), { priority });
    setFeedbackItems(prev => prev.map(f => f.id === id ? { ...f, priority } : f));
  };

  const saveFeedbackNote = async (id) => {
    setSavingNote(id);
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'feedback', id), { adminNotes: noteText });
    setFeedbackItems(prev => prev.map(f => f.id === id ? { ...f, adminNotes: noteText } : f));
    setSavingNote(null);
    setExpandedFeedback(null);
    setNoteText('');
  };

  // ── Add-to-product modal ──────────────────────────────────────
  const openAddProduct = (receiptText, storeId) => {
    const size  = parseSize(receiptText);
    const brand = parseBrand(receiptText);
    // Strip size from the end of the text for a cleaner product name
    const cleanName = receiptText.replace(new RegExp(`\\s*${size}\\s*$`, 'i'), '').trim();
    const storeDisplay = STORE_DISPLAY_NAMES[storeId] || '';
    setApForm({
      productName:   cleanName,
      brand,
      size,
      store:         storeDisplay,
      category:      '',
      subcategory:   '',
      variant:       '',
      price:         '',
    });
    setApCopied(false);
    setAddProductModal({ receiptText, storeId });
  };

  const handleApChange = (field, value) => setApForm(f => ({ ...f, [field]: value }));

  const handleCopyRow = () => {
    const slug = apForm.productName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const row = buildSheetRow({
      productId:       `prod_${slug}`,
      productName:     apForm.productName,
      category:        apForm.category,
      subcategory:     apForm.subcategory,
      standardizedSize: apForm.size,
      unitType:        '',
      brand:           apForm.brand,
      storeProductName: addProductModal?.receiptText || apForm.productName,
      variant:         apForm.variant,
      storeSize:       apForm.size.replace(/[^0-9.]/g, ''),
      store:           apForm.store,
      price:           apForm.price,
    });
    navigator.clipboard.writeText(row).then(() => {
      setApCopied(true);
      setTimeout(() => setApCopied(false), 3000);
    });
  };

  const handleDeleteProduct = async (productId) => {
    setDeleting(productId);
    const result = await deleteProduct(productId);
    if (result.success) setProducts(prev => prev.filter(p => p.id !== productId));
    setDeleting(null);
    setConfirmDelete(null);
  };

  const handleApprove = async (review) => {
    const adminUid = auth.currentUser?.uid ?? 'admin';
    setActioning(prev => new Set([...prev, review.id]));
    const result = await approvePriceReview(review.id, review, adminUid);
    if (result.success) {
      setReviews(prev => prev.map(r => r.id === review.id ? { ...r, status: 'approved' } : r));
    }
    setActioning(prev => { const s = new Set(prev); s.delete(review.id); return s; });
  };

  const handleReject = async (review) => {
    const adminUid = auth.currentUser?.uid ?? 'admin';
    setActioning(prev => new Set([...prev, review.id]));
    const result = await rejectPriceReview(review.id, adminUid);
    if (result.success) {
      setReviews(prev => prev.map(r => r.id === review.id ? { ...r, status: 'rejected' } : r));
    }
    setActioning(prev => { const s = new Set(prev); s.delete(review.id); return s; });
  };

  const handleBatchApprove = async () => {
    const adminUid = auth.currentUser?.uid ?? 'admin';
    setBatching(true);
    setBatchConfirm(false);
    const result = await batchApprovePriceReviews(reviews, threshold, adminUid);
    if (result.succeeded > 0) await loadReviews();
    setBatching(false);
  };

  const pendingReviews = reviews.filter(r => r.status === 'pending_review');
  const batchEligible  = pendingReviews.filter(r =>
    !r.isPromo &&
    r.proposedPricePence != null &&
    r.currentStoredPricePence != null &&
    Math.abs(r.proposedPricePence - r.currentStoredPricePence) / r.currentStoredPricePence <= threshold
  );

  const visibleReviews = reviews.filter(r => {
    if (reviewFilter === 'pending')  return r.status === 'pending_review';
    if (reviewFilter === 'approved') return r.status === 'approved';
    if (reviewFilter === 'rejected') return r.status === 'rejected';
    if (reviewFilter === 'community') return r.sourceType === 'community_manual';
    if (reviewFilter === 'system')    return r.sourceType === 'system_fetch';
    if (reviewFilter === 'receipt')   return r.sourceType === 'receipt_ocr';
    return true;
  });

  const deriveSource = (confirmedByUid) => {
    if (!confirmedByUid) return 'unknown';
    if (confirmedByUid.startsWith('seed:')) return 'seeded';
    if (confirmedByUid.startsWith('gpt:'))  return 'gpt';
    return 'manual';
  };

  return (
    <div className="admin-page">

      {/* ── Post-creation copy toast (fixed, survives tab changes) ── */}
      {createdCopy && (
        <div className="admin-created-copy-bar">
          <div className="admin-created-copy-text">
            <span className="admin-created-copy-tick">✓</span>
            <strong>{createdCopy.name}</strong> created
            <span className="admin-created-copy-id">{createdCopy.productId}</span>
          </div>
          <div className="admin-created-copy-actions">
            <button className="admin-created-copy-btn" onClick={handleCopyCreatedRow}>
              {copyConfirmed ? '✓ Copied!' : 'Copy sheet row'}
            </button>
            <button className="admin-created-copy-dismiss" onClick={() => { setCreatedCopy(null); setCopyConfirmed(false); }}>✕</button>
          </div>
        </div>
      )}

      {/* ── Header ── */}
      <div className="admin-header">
        <button className="admin-back-btn" onClick={onBack} aria-label="Back">
          <svg width="18" height="18" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="13 18 7 12 13 6"/>
          </svg>
        </button>
        <div className="admin-title-group">
          <h1 className="admin-title">{SECTIONS[adminSection].label}</h1>
          <p className="admin-subtitle">{TAB_SUBTITLES[adminTab]}</p>
        </div>
        {(adminTab === 'reviews' || adminTab === 'receipts' || adminTab === 'feedback') && (
          <button
            className="admin-refresh-btn"
            onClick={adminTab === 'reviews' ? loadReviews : adminTab === 'receipts' ? loadReceipts : loadFeedback}
            aria-label="Refresh"
            title="Refresh"
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
        )}
      </div>

      {/* ── Top-level section nav ── */}
      <div className="admin-section-nav">
        {Object.entries(SECTIONS).map(([key, sec]) => {
          const queueBadge = key === 'queue' ? (pendingReviews.length + requestsBadge) : 0;
          const feedbackBadge = key === 'feedback' ? feedbackItems.filter(f => f.status === 'new').length : 0;
          const badge = queueBadge || feedbackBadge;
          return (
            <button
              key={key}
              className={`admin-section-btn${adminSection === key ? ' active' : ''}`}
              onClick={() => goToSection(key)}
            >
              {sec.label}
              {badge > 0 && <span className="admin-tab-badge">{badge}</span>}
            </button>
          );
        })}
      </div>

      {/* ── Sub-tabs (only shown when section has more than one tab) ── */}
      {SECTIONS[adminSection].tabs.length > 1 && (
        <div className="admin-tab-bar">
          {SECTIONS[adminSection].tabs.map(tab => {
            const tabBadge =
              tab === 'reviews'  ? pendingReviews.length :
              tab === 'requests' ? requestsBadge : 0;
            return (
              <button
                key={tab}
                className={`admin-tab-btn${adminTab === tab ? ' active' : ''}`}
                onClick={() => setAdminTab(tab)}
              >
                {TAB_LABELS[tab]}
                {tabBadge > 0 && <span className="admin-tab-badge">{tabBadge}</span>}
              </button>
            );
          })}
        </div>
      )}

      {/* ── Dashboard ── */}
      {adminTab === 'dashboard' && (
        <div className="admin-dashboard">

          {/* ── Time window picker ── */}
          <div style={{ display: 'flex', gap: 8, padding: '0 0 16px', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--text-muted, #666)', fontWeight: 500 }}>Period:</span>
            {[7, 30].map(d => (
              <button
                key={d}
                onClick={() => { setDashboardWindow(d); loadDashboard(d); }}
                style={{
                  padding: '4px 12px', borderRadius: 20, border: '1px solid',
                  borderColor: dashboardWindow === d ? 'var(--deep-purple)' : '#ddd',
                  background: dashboardWindow === d ? 'var(--deep-purple)' : 'transparent',
                  color: dashboardWindow === d ? '#fff' : 'inherit',
                  fontSize: '0.8rem', cursor: 'pointer',
                }}
              >Last {d}d</button>
            ))}
            <button
              onClick={() => loadDashboard(dashboardWindow)}
              style={{ marginLeft: 'auto', fontSize: '0.8rem', background: 'none', border: 'none', color: 'var(--deep-purple)', cursor: 'pointer' }}
              disabled={dashboardLoading}
            >
              {dashboardLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {/* ── Active users ── */}
          <div className="admin-presence">
            <div className="admin-presence-header">
              <h3 className="admin-presence-title">
                <span className="admin-presence-dot" /> Active users
                <span className="admin-presence-window">(last 5 min)</span>
              </h3>
              <button className="admin-presence-refresh" onClick={fetchPresence} disabled={presenceLoading}>
                {presenceLoading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
            {presenceLoading && activeUsers.length === 0 ? (
              <p className="admin-presence-empty">Loading…</p>
            ) : activeUsers.length === 0 ? (
              <p className="admin-presence-empty">No active users right now.</p>
            ) : (
              <>
                <div className="admin-presence-summary">
                  <span className="admin-presence-count">{activeUsers.length}</span> online
                  &nbsp;·&nbsp;
                  {activeUsers.filter(u => !u.isAnonymous).length} signed in
                  &nbsp;·&nbsp;
                  {activeUsers.filter(u => u.isAnonymous).length} guests
                </div>
                <div className="admin-presence-rows">
                  {activeUsers.map(u => {
                    const isTest = testUserIds.includes(u.uid);
                    return (
                      <div key={u.uid} className={`admin-presence-row${isTest ? ' admin-presence-row--test' : ''}`}>
                        <span className={`admin-presence-badge ${u.isAnonymous ? 'admin-presence-badge--guest' : 'admin-presence-badge--user'}`}>
                          {u.isAnonymous ? 'Guest' : 'User'}
                        </span>
                        {isTest && (
                          <span className="admin-presence-badge admin-presence-badge--test">TEST</span>
                        )}
                        <span className="admin-presence-page">{u.page ?? '—'}</span>
                        <span className="admin-presence-uid">{u.uid.slice(0, 8)}…</span>
                        <button
                          className={`admin-presence-test-toggle${isTest ? ' admin-presence-test-toggle--active' : ''}`}
                          onClick={() => handleToggleTestUser(u.uid)}
                          title={isTest ? 'Remove test tag (include in metrics)' : 'Mark as test user (exclude from metrics)'}
                        >
                          {isTest ? 'Untag' : 'Tag test'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
            {presenceLastFetched && (
              <p className="admin-presence-ts">Last updated {presenceLastFetched.toLocaleTimeString('en-GB')}</p>
            )}
          </div>

          {/* ── Test users ── */}
          <div className="admin-test-users">
            <div className="admin-test-users-header">
              <span className="admin-test-users-title">Test users (excluded from metrics)</span>
            </div>
            {testUserIds.length === 0 ? (
              <p className="admin-test-users-empty">No test users tagged. Use "Tag test" on an active user row above.</p>
            ) : (
              <div className="admin-test-users-list">
                {testUserIds.map(uid => (
                  <div key={uid} className="admin-test-users-row">
                    <span className="admin-presence-uid" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{uid}</span>
                    <button
                      className="admin-presence-test-toggle admin-presence-test-toggle--active"
                      onClick={() => handleToggleTestUser(uid)}
                    >
                      Untag
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* ── Comparison quality ── */}
          <div style={{ marginTop: 24 }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: 12, color: 'var(--text-primary, #111)' }}>
              Comparison quality
            </h3>
            {dashboardLoading && !dashboardMetrics ? (
              <p className="admin-loading">Loading metrics…</p>
            ) : !dashboardMetrics ? null : (
              <>
                {/* Stat cards */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
                  {[
                    { label: 'Receipts processed',     value: dashboardMetrics.receiptsProcessed },
                    { label: 'Comparisons viewed',     value: dashboardMetrics.comparisonsViewed },
                    { label: 'Meaningful receipt rate', value: `${Math.round(dashboardMetrics.meaningfulReceiptRate * 100)}%` },
                    { label: 'Avg matched items',      value: dashboardMetrics.avgMatchedItems.toFixed(1) },
                    { label: 'Avg match rate',         value: `${Math.round(dashboardMetrics.avgMatchRate * 100)}%` },
                    { label: 'Meaningful comparisons', value: dashboardMetrics.meaningfulComparisons },
                  ].map(({ label, value }) => (
                    <div key={label} style={{
                      background: '#f7f7f9', borderRadius: 10, padding: '12px 14px',
                      border: '1px solid rgba(0,0,0,0.06)',
                    }}>
                      <p style={{ fontSize: '0.72rem', color: '#666', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
                      <p style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0, color: 'var(--deep-purple)' }}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* Match distribution */}
                <div style={{ background: '#f7f7f9', borderRadius: 10, padding: '12px 14px', border: '1px solid rgba(0,0,0,0.06)' }}>
                  <p style={{ fontSize: '0.72rem', color: '#666', margin: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Match distribution (items per receipt)</p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                    {Object.entries(dashboardMetrics.distribution).map(([range, count]) => (
                      <div key={range} style={{ textAlign: 'center' }}>
                        <p style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 2px', color: 'var(--deep-purple)' }}>{count}</p>
                        <p style={{ fontSize: '0.7rem', color: '#888', margin: 0 }}>{range}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Phase 1 north star */}
                <div style={{ marginTop: 10, padding: '10px 14px', background: '#f0ecf8', borderRadius: 10, border: '1px solid rgba(106,13,173,0.12)' }}>
                  <p style={{ fontSize: '0.75rem', color: '#6a0dad', margin: 0 }}>
                    <strong>Phase 1 goal:</strong> ≥50% of new users reach a meaningful comparison (≥5 items matched or ≥50% match rate).
                    {dashboardMetrics.receiptsProcessed > 0 && (
                      <> Current: <strong>{Math.round(dashboardMetrics.meaningfulReceiptRate * 100)}%</strong> of receipts are meaningful.</>
                    )}
                  </p>
                </div>
              </>
            )}
          </div>

        </div>
      )}

      {/* ── Receipts tab ── */}
      {adminTab === 'receipts' && (
        <div className="admin-receipts">
          {receiptsError ? (
            <div className="admin-empty">
              <p className="admin-empty-title">Permission error</p>
              <p className="admin-empty-sub" style={{ fontFamily: 'monospace', wordBreak: 'break-all', fontSize: '0.75rem' }}>
                Your current UID: <strong>{auth.currentUser?.uid ?? '—'}</strong>
              </p>
              <p className="admin-empty-sub">Add this UID to Firestore → users → &#123;uid&#125; with <code>role: "admin"</code></p>
            </div>
          ) : receiptsLoading ? (
            <div className="admin-loading">Loading receipts…</div>
          ) : receiptsList.length === 0 ? (
            <div className="admin-empty">
              <p className="admin-empty-title">No receipts yet</p>
              <p className="admin-empty-sub">Scanned receipts will appear here once processed.</p>
            </div>
          ) : (
            <>
              {/* Filter toggle */}
              <div className="admin-receipts-filter">
                <button
                  className={`admin-receipts-filter-btn${receiptsFilter === 'new' ? ' active' : ''}`}
                  onClick={() => setReceiptsFilter('new')}
                >
                  New
                  {receiptsList.filter(r => !r.reviewed).length > 0 && (
                    <span className="admin-receipts-filter-count">
                      {receiptsList.filter(r => !r.reviewed).length}
                    </span>
                  )}
                </button>
                <button
                  className={`admin-receipts-filter-btn${receiptsFilter === 'reviewed' ? ' active' : ''}`}
                  onClick={() => setReceiptsFilter('reviewed')}
                >
                  Reviewed
                </button>
              </div>

              {(() => {
                // Build groups from the filtered (new/reviewed) receipts
                const filtered = receiptsList.filter(r =>
                  receiptsFilter === 'reviewed' ? r.reviewed : !r.reviewed
                );

                if (filtered.length === 0) return (
                  <div className="admin-empty" style={{ padding: '32px 16px' }}>
                    <p className="admin-empty-title">
                      {receiptsFilter === 'reviewed' ? 'Nothing reviewed yet' : 'All receipts reviewed'}
                    </p>
                    <p className="admin-empty-sub">
                      {receiptsFilter === 'new' ? 'Switch to Reviewed to see past sessions.' : ''}
                    </p>
                  </div>
                );

                // Group receipts by key, preserving recency order within each group
                const groupMap = {};
                for (const r of filtered) {
                  const key = deriveGroupKey(r);
                  if (!groupMap[key]) groupMap[key] = [];
                  groupMap[key].push(r);
                }
                const groups = Object.entries(groupMap)
                  .map(([key, receipts]) => ({ key, receipts, ...deriveGroupMeta(key) }))
                  .sort((a, b) => a.priority - b.priority);

                return (
                  <div className="admin-receipt-groups">
                    {groups.map(group => {
                      const isExpanded  = expandedGroup === group.key;
                      const isMarking   = markingGroup  === group.key;
                      const autoMarks   = group.priority <= 2;
                      // Show up to 5 receipts in expanded view; sort by recency
                      const sample = [...group.receipts]
                        .sort((a, b) => (b.processedAt || 0) - (a.processedAt || 0))
                        .slice(0, 5);

                      return (
                        <div key={group.key} className={`admin-receipt-group admin-receipt-group--${group.type}`}>
                          {/* ── Group header row ── */}
                          <div
                            className="admin-receipt-group-header"
                            onClick={() => setExpandedGroup(isExpanded ? null : group.key)}
                          >
                            <span className="admin-receipt-status">{group.emoji}</span>
                            <span className="admin-receipt-store">{group.label}</span>
                            <span className="admin-receipt-group-count">{group.receipts.length}</span>
                            <span className="admin-receipt-group-spacer" />
                            {group.action && (
                              <span
                                className={`admin-receipt-action${autoMarks ? ' admin-receipt-action--clickable' : ''}`}
                                onClick={autoMarks ? (e) => { e.stopPropagation(); markGroupReviewed(group.key, group.receipts); } : undefined}
                                title={autoMarks ? 'Click to mark all as reviewed' : undefined}
                              >
                                {group.action}
                              </span>
                            )}
                            <button
                              className="admin-receipt-review-btn"
                              disabled={isMarking}
                              onClick={(e) => { e.stopPropagation(); markGroupReviewed(group.key, group.receipts, !group.receipts[0]?.reviewed); }}
                              title="Mark all as reviewed"
                            >
                              {isMarking ? '…' : receiptsFilter === 'reviewed' ? '↩ all' : '✓ all'}
                            </button>
                            <span className={`admin-receipt-group-chevron${isExpanded ? ' open' : ''}`}>›</span>
                          </div>

                          {/* ── Expanded receipt list ── */}
                          {isExpanded && (
                            <div className="admin-receipt-group-items">
                              {sample.map(r => {
                                const dateStr = r.processedAt
                                  ? r.processedAt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                                  : '—';
                                const isReviewing = reviewingReceipt === r.id;
                                return (
                                  <div key={r.id} className="admin-receipt-group-item">
                                    <span className="admin-receipt-counts">
                                      {r.itemCount} item{r.itemCount !== 1 ? 's' : ''}
                                      {r.matchedCount > 0 && ` · ${r.matchedCount} matched`}
                                    </span>
                                    <span className="admin-receipt-label" title={
                                      [
                                        r.parserMode && `parser: ${r.parserMode}`,
                                        r.fallbackReason && `fallback: ${r.fallbackReason}`,
                                        r.prePairTriggered && 'pre-pair: triggered',
                                      ].filter(Boolean).join(' | ')
                                    }>
                                      {r.parserMode || '—'}
                                    </span>
                                    <span className="admin-receipt-date">{dateStr}</span>
                                    <button
                                      className="admin-receipt-review-btn"
                                      onClick={() => markReceiptReviewed(r.id, !r.reviewed)}
                                      disabled={isReviewing}
                                      title={r.reviewed ? 'Mark as new' : 'Mark as reviewed'}
                                    >
                                      {isReviewing ? '…' : r.reviewed ? '↩' : '✓'}
                                    </button>
                                  </div>
                                );
                              })}
                              {group.receipts.length > 5 && (
                                <p className="admin-receipt-group-more">
                                  +{group.receipts.length - 5} more
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}

      {/* ── Feedback tab ── */}
      {adminTab === 'feedback' && (() => {
        // null = default active view (new + reviewed, archived hidden)
        const visibleFeedback = feedbackItems.filter(f => {
          const statusOk =
            feedbackStatusFilter === 'all'  ? true :
            feedbackStatusFilter === null   ? (f.status === 'new' || f.status === 'reviewed') :
            f.status === feedbackStatusFilter;
          const typeOk = feedbackTypeFilter === 'all' || f.type === feedbackTypeFilter;
          const prioOk = feedbackPrioFilter === 'all' || f.priority === feedbackPrioFilter;
          return statusOk && typeOk && prioOk;
        });

        const countFor = s => feedbackItems.filter(f => f.status === s).length;
        const hasSecondaryFilter = feedbackTypeFilter !== 'all' || feedbackPrioFilter !== 'all';
        const segmentOptions = [
          { val: 'new',      label: 'New',      count: countFor('new') },
          { val: 'reviewed', label: 'Reviewed', count: countFor('reviewed') },
          { val: 'actioned', label: 'Actioned', count: countFor('actioned') },
          { val: 'archived', label: 'Archived', count: countFor('archived') },
        ];

        return (
          <div className="admin-feedback">
            {feedbackError ? (
              <div className="admin-empty"><p className="admin-empty-title">Error loading feedback</p></div>
            ) : feedbackLoading ? (
              <div className="admin-loading">Loading feedback…</div>
            ) : (
              <>
                {/* ── Controls: two rows max ── */}
                <div className="admin-feedback-controls">

                  {/* Row 1 — status segmented control */}
                  <div className="admin-feedback-segment">
                    {segmentOptions.map(({ val, label, count }) => (
                      <button
                        key={val}
                        className={`admin-feedback-segment-btn${feedbackStatusFilter === val ? ' active' : ''}`}
                        onClick={() => setFeedbackStatusFilter(feedbackStatusFilter === val ? null : val)}
                      >
                        {label}
                        {count > 0 && <span className="seg-count">{count}</span>}
                      </button>
                    ))}
                  </div>

                  {/* Row 2 — type + priority dropdowns + clear */}
                  <div className="admin-feedback-filterrow">
                    <select className="admin-feedback-select" value={feedbackTypeFilter} onChange={e => setFeedbackTypeFilter(e.target.value)}>
                      <option value="all">Type: All</option>
                      <option value="bug">🐛 Bug</option>
                      <option value="idea">💡 Idea</option>
                      <option value="general">💬 Thoughts</option>
                    </select>
                    <select className="admin-feedback-select" value={feedbackPrioFilter} onChange={e => setFeedbackPrioFilter(e.target.value)}>
                      <option value="all">Priority: All</option>
                      <option value="high">🔴 High</option>
                      <option value="medium">🟡 Medium</option>
                      <option value="low">⚪ Low</option>
                    </select>
                    {hasSecondaryFilter && (
                      <button className="admin-feedback-clear" onClick={() => { setFeedbackTypeFilter('all'); setFeedbackPrioFilter('all'); }}>
                        Clear
                      </button>
                    )}
                    {feedbackStatusFilter !== 'all' && (
                      <button className="admin-feedback-showall" onClick={() => setFeedbackStatusFilter('all')}>
                        Show all
                      </button>
                    )}
                  </div>

                </div>

                <div className="admin-feedback-divider" />

                {visibleFeedback.length === 0 ? (
                  <div className="admin-empty" style={{ paddingTop: 48 }}>
                    <p className="admin-empty-title">
                      {feedbackItems.length === 0 ? 'No feedback yet' : 'Nothing matches these filters'}
                    </p>
                    <p className="admin-empty-sub">
                      {feedbackItems.length === 0
                        ? 'Submissions from users will appear here.'
                        : 'Try a different status or clear the filters.'}
                    </p>
                  </div>
                ) : (
                  <div className="admin-feedback-list" style={{ padding: '12px 16px 0' }}>
                    {visibleFeedback.map(item => {
                      const isOpen  = expandedFeedback === item.id;
                      const ts      = item.createdAt?.toDate?.();
                      const dateStr = ts ? ts.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—';
                      return (
                        <div key={item.id} className={`admin-feedback-card${isOpen ? ' open' : ''}${item.status === 'archived' ? ' archived' : ''}`}>

                          {/* Collapsed row */}
                          <div className="admin-feedback-card-header" onClick={() => {
                            setExpandedFeedback(isOpen ? null : item.id);
                            setNoteText(item.adminNotes || '');
                          }}>
                            <div className="admin-feedback-card-meta">
                              <span className="admin-feedback-type">{FEEDBACK_TYPE_LABELS[item.type] || item.type}</span>
                              {item.priority && (
                                <span className="admin-feedback-prio">{FEEDBACK_PRIORITY_LABELS[item.priority]}</span>
                              )}
                              {item.context?.page && (
                                <span className="admin-feedback-page">{item.context.page}</span>
                              )}
                              <span className="admin-feedback-status" style={{ color: FEEDBACK_STATUS_COLORS[item.status] || '#999' }}>
                                {FEEDBACK_STATUS_LABELS[item.status] || item.status}
                              </span>
                            </div>
                            <span className="admin-feedback-date">{dateStr}</span>
                          </div>

                          {(() => {
                            const content = formatFeedbackContent(item);
                            if (!content) return null;
                            // Multi-line structured responses (beta feedback)
                            const lines = content.split('\n').filter(Boolean);
                            if (lines.length > 1) {
                              return (
                                <div className="admin-feedback-message admin-feedback-structured">
                                  {lines.map((line, i) => <p key={i} className="admin-feedback-structured-line">{line}</p>)}
                                </div>
                              );
                            }
                            return <p className="admin-feedback-message">"{content}"</p>;
                          })()}

                          {isOpen && (
                            <div className="admin-feedback-actions">

                              {/* Status row */}
                              <div className="admin-feedback-action-label">Status</div>
                              <div className="admin-feedback-status-row">
                                {['new', 'reviewed', 'actioned'].map(s => (
                                  <button
                                    key={s}
                                    className={`admin-feedback-status-btn${item.status === s ? ' active' : ''}`}
                                    onClick={() => updateFeedbackStatus(item.id, s)}
                                    style={item.status === s ? { borderColor: FEEDBACK_STATUS_COLORS[s], color: FEEDBACK_STATUS_COLORS[s] } : {}}
                                  >
                                    {FEEDBACK_STATUS_LABELS[s]}
                                  </button>
                                ))}
                                <button
                                  className={`admin-feedback-status-btn archive-btn${item.status === 'archived' ? ' active' : ''}`}
                                  onClick={() => updateFeedbackStatus(item.id, item.status === 'archived' ? 'reviewed' : 'archived')}
                                  style={item.status === 'archived' ? { borderColor: FEEDBACK_STATUS_COLORS.archived, color: FEEDBACK_STATUS_COLORS.archived } : {}}
                                >
                                  {item.status === 'archived' ? 'Unarchive' : 'Done / Archive'}
                                </button>
                              </div>

                              {/* Priority row */}
                              <div className="admin-feedback-action-label">Priority</div>
                              <div className="admin-feedback-status-row">
                                {['high', 'medium', 'low'].map(p => (
                                  <button
                                    key={p}
                                    className={`admin-feedback-status-btn${item.priority === p ? ' active' : ''}`}
                                    onClick={() => updateFeedbackPriority(item.id, item.priority === p ? null : p)}
                                  >
                                    {FEEDBACK_PRIORITY_LABELS[p]}
                                  </button>
                                ))}
                              </div>

                              {/* Notes */}
                              <div className="admin-feedback-action-label">Note</div>
                              <textarea
                                className="admin-feedback-note-input"
                                placeholder="Add a note…"
                                value={noteText}
                                onChange={e => setNoteText(e.target.value)}
                                rows={2}
                              />
                              <button
                                className="admin-feedback-save-note"
                                onClick={() => saveFeedbackNote(item.id)}
                                disabled={savingNote === item.id}
                              >
                                {savingNote === item.id ? 'Saving…' : 'Save note'}
                              </button>
                              {item.adminNotes && !isOpen && (
                                <p className="admin-feedback-note-preview">📝 {item.adminNotes}</p>
                              )}
                              {item.userEmail && (
                                <p className="admin-feedback-user">From: {item.userEmail}</p>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}

      {/* ── Tools tab: agent ── */}
      {adminTab === 'agent' && (
        <ProductDataAgent />
      )}

      {adminTab === 'aliases' && (
        <div className="admin-aliases">

          {/* ── Inner mode tab bar ── */}
          <div className="admin-alias-mode-bar">
            <button
              className={`admin-alias-mode-btn${aliasMode === 'review' ? ' active' : ''}`}
              onClick={() => setAliasMode('review')}
            >
              Review
              {reviewItems.filter(i => {
                const k = `${i.productText}|${i.storeId || ''}`;
                return !reviewActions[k];
              }).length > 0 && (
                <span className="admin-alias-mode-badge">
                  {reviewItems.filter(i => {
                    const k = `${i.productText}|${i.storeId || ''}`;
                    return !reviewActions[k];
                  }).length}
                </span>
              )}
            </button>
            <button
              className={`admin-alias-mode-btn${aliasMode === 'unmatched' ? ' active' : ''}`}
              onClick={() => setAliasMode('unmatched')}
            >
              Unmatched
              {unmatchedItems.filter(i => {
                const k = `${i.productText}|${i.storeId || ''}`;
                return !unmatchedActions[k];
              }).length > 0 && (
                <span className="admin-alias-mode-badge">
                  {unmatchedItems.filter(i => {
                    const k = `${i.productText}|${i.storeId || ''}`;
                    return !unmatchedActions[k];
                  }).length}
                </span>
              )}
            </button>
            <button
              className={`admin-alias-mode-btn${aliasMode === 'aliases' ? ' active' : ''}`}
              onClick={() => setAliasMode('aliases')}
            >
              Aliases
            </button>
          </div>

          {/* ════════════════════════════════════════════════════════
              REVIEW MODE — fix wrong matches from recent receipts
          ════════════════════════════════════════════════════════ */}
          {aliasMode === 'review' && (
            <>
              <p className="admin-aliases-section-sub" style={{ padding: '10px 16px 4px' }}>
                Matched items from recent receipts, lowest confidence first. Confirm correct matches or fix wrong ones.
              </p>

              {reviewLoading ? (
                <p className="admin-empty" style={{ padding: '24px 16px' }}>Loading…</p>
              ) : reviewItems.length === 0 ? (
                <div className="admin-empty" style={{ padding: '32px 16px' }}>
                  <p className="admin-empty-title">No matched items yet</p>
                  <p className="admin-empty-sub">Scan a receipt and matched items will appear here.</p>
                </div>
              ) : (
                <div className="admin-review-list">
                  {reviewItems.map(item => {
                    const key    = `${item.productText}|${item.storeId || ''}`;
                    const action = reviewActions[key];
                    const isFixingThis = reviewFixItem &&
                      reviewFixItem.productText === item.productText &&
                      reviewFixItem.storeId === item.storeId;

                    // Confidence badge
                    const confClass =
                      item.matchType === 'alias'        ? 'alias'  :
                      item.confidenceScore >= 0.85      ? 'high'   :
                      item.confidenceScore >= 0.60      ? 'medium' : 'low';
                    const confLabel =
                      item.matchType === 'alias' ? 'alias' :
                      item.confidenceScore != null ? `${Math.round(item.confidenceScore * 100)}%` : item.confidence || '?';

                    return (
                      <div key={key} className="admin-review-item">
                        <div className="admin-review-match">
                          <span className="admin-review-receipt">{item.productText}</span>
                          <span className="admin-review-arrow">→</span>
                          <span className="admin-review-product">{item.matchedProductName || item.matchedProductId}</span>
                          <span className={`admin-review-conf admin-review-conf--${confClass}`}>
                            {item.matchType === 'alias' ? 'alias' : `${item.matchType} · ${confLabel}`}
                          </span>
                        </div>

                        {item.storeId && (
                          <span className="admin-alias-store" style={{ fontSize: '11px', marginLeft: 0 }}>
                            {item.storeId.replace('store_', '')}
                          </span>
                        )}

                        {action === 'confirmed' && <p className="admin-alias-done-note">✓ Confirmed</p>}
                        {action === 'fixed'     && <p className="admin-alias-done-note">✓ Corrected</p>}
                        {action === 'ignored'   && <p className="admin-alias-done-note">Ignored</p>}

                        {!action && (
                          <div className="admin-review-actions">
                            <button
                              className="admin-review-confirm-btn"
                              onClick={() => handleReviewConfirm(item)}
                              disabled={reviewActions[key] === 'confirming'}
                            >
                              {reviewActions[key] === 'confirming' ? '…' : '✔ Correct'}
                            </button>
                            <button
                              className="admin-review-fix-btn"
                              onClick={() => {
                                setReviewFixItem(isFixingThis ? null : item);
                                setReviewFixSearch('');
                                setReviewFixResults([]);
                              }}
                            >
                              {isFixingThis ? 'Cancel' : '✏ Fix'}
                            </button>
                            <button
                              className="admin-review-ignore-btn"
                              onClick={() => handleIgnoreReview(item)}
                              disabled={reviewActions[key] === 'ignoring'}
                            >
                              {reviewActions[key] === 'ignoring' ? '…' : 'Ignore'}
                            </button>
                          </div>
                        )}

                        {isFixingThis && !action && (
                          <div className="admin-alias-match-panel">
                            {/* ── Reassign to existing product ── */}
                            <input
                              className="admin-aliases-search"
                              type="text"
                              placeholder="Search for correct product…"
                              value={reviewFixSearch}
                              onChange={e => handleReviewFixSearch(e.target.value)}
                              autoFocus
                            />
                            {reviewFixLoading && <p className="admin-alias-match-loading">Searching…</p>}
                            {reviewFixResults.length > 0 && (
                              <div className="admin-alias-match-results">
                                {reviewFixResults.map(p => (
                                  <button
                                    key={p.id}
                                    className="admin-alias-match-result"
                                    onClick={() => handleSaveReviewFix(item, p)}
                                  >
                                    <span className="admin-alias-match-name">{p.name}</span>
                                    <span className="admin-alias-match-id">{p.id}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {/* ── Create new product ── */}
                            {!(createPanel && createPanel.item.productText === item.productText && createPanel.sourceType === 'review') && (
                              <>
                                <div className="admin-create-divider"><span>or</span></div>
                                <button
                                  className="admin-create-open-btn"
                                  onClick={() => handleOpenCreate(item, 'review')}
                                >
                                  + Create new product
                                </button>
                              </>
                            )}
                            {createPanel && createPanel.item.productText === item.productText && createPanel.sourceType === 'review' && (
                              <div className="admin-create-product-form">
                                <div className="admin-create-form-header">
                                  <span className="admin-create-form-title">New product</span>
                                  <button className="admin-create-back" onClick={() => setCreatePanel(null)}>✕</button>
                                </div>
                                {findSimilarProducts(createPanel.form.name, allProducts).length > 0 && (
                                  <div className="admin-create-similar">
                                    <span className="admin-create-similar-label">⚠ Similar products exist — use one of these instead?</span>
                                    {findSimilarProducts(createPanel.form.name, allProducts).map(p => (
                                      <button key={p.id} className="admin-create-similar-item" onClick={() => handleSaveReviewFix(item, p)}>
                                        {p.name} <span className="admin-alias-match-id">{p.id}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                                <div className="admin-create-fields">
                                  <label className="admin-create-label">
                                    Name <span className="admin-create-hint">Generic — no store, no brand, no size</span>
                                    <input className="admin-create-input" value={createPanel.form.name} placeholder="e.g. Spreadable Butter" onChange={e => handleCreateFormChange('name', e.target.value)} autoFocus />
                                  </label>
                                  <label className="admin-create-label">Category
                                    <select className="admin-create-input" value={createPanel.form.category} onChange={e => handleCreateFormChange('category', e.target.value)}>
                                      <option value="">— none —</option>
                                      {ALL_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                  </label>
                                  <label className="admin-create-label">Subcategory
                                    <select className="admin-create-input" value={createPanel.form.subcategory} onChange={e => handleCreateFormChange('subcategory', e.target.value)}>
                                      <option value="">— none —</option>
                                      {ALL_SUBCATEGORIES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                  </label>
                                  <label className="admin-create-label">
                                    Type <span className="admin-create-hint">e.g. spreadable · salted · unsalted · semi_skimmed</span>
                                    <input className="admin-create-input" value={createPanel.form.variant} onChange={e => handleCreateFormChange('variant', e.target.value)} placeholder="leave blank if none" />
                                  </label>
                                  <label className="admin-create-label">
                                    Storage type <span className="admin-create-hint">chilled (fridge) or ambient (shelf)</span>
                                    <select className="admin-create-input" value={createPanel.form.storageType} onChange={e => handleCreateFormChange('storageType', e.target.value)}>
                                      <option value="">— not applicable —</option>
                                      <option value="chilled">chilled</option>
                                      <option value="ambient">ambient</option>
                                      <option value="frozen">frozen</option>
                                    </select>
                                  </label>
                                  <div className="admin-create-label">
                                    Brand
                                    <div className="admin-create-brand-toggle">
                                      <button
                                        type="button"
                                        className={`admin-create-brand-opt${createPanel.form.ownBrand ? '' : ' active'}`}
                                        onClick={() => { handleCreateFormChange('ownBrand', false); handleCreateFormChange('brand', ''); }}
                                      >Branded</button>
                                      <button
                                        type="button"
                                        className={`admin-create-brand-opt${createPanel.form.ownBrand ? ' active' : ''}`}
                                        onClick={() => { handleCreateFormChange('ownBrand', true); handleCreateFormChange('brand', ''); }}
                                      >Own brand</button>
                                    </div>
                                    {!createPanel.form.ownBrand && (
                                      <input
                                        className="admin-create-input"
                                        value={createPanel.form.brand}
                                        onChange={e => handleCreateFormChange('brand', e.target.value)}
                                        placeholder="e.g. Oatly, Heinz, Lurpak"
                                      />
                                    )}
                                  </div>
                                  <label className="admin-create-label">
                                    Product ID <span className="admin-create-hint">auto-generated — edit if needed</span>
                                    <input className="admin-create-input admin-create-input--id" value={createPanel.form.productId} onChange={e => handleCreateFormChange('productId', e.target.value)} spellCheck={false} />
                                  </label>
                                </div>
                                {createError && <p className="admin-create-error">{createError}</p>}
                                <button className="admin-create-btn" onClick={handleCreateAndLink} disabled={createSaving}>
                                  {createSaving ? 'Creating…' : 'Create & link alias'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* ════════════════════════════════════════════════════════
              UNMATCHED MODE — items with no match, turn into rules
          ════════════════════════════════════════════════════════ */}
          {aliasMode === 'unmatched' && (
            <>
              {/* Queue / Backlog sub-tabs */}
              <div className="admin-unmatched-subtabs">
                <button
                  className={`admin-unmatched-subtab${unmatchedMode === 'queue' ? ' active' : ''}`}
                  onClick={() => setUnmatchedMode('queue')}
                >
                  Queue
                  {unmatchedItems.filter(i => !unmatchedActions[`${i.productText}|${i.storeId || ''}`]).length > 0 && (
                    <span className="admin-alias-mode-badge">
                      {unmatchedItems.filter(i => !unmatchedActions[`${i.productText}|${i.storeId || ''}`]).length}
                    </span>
                  )}
                </button>
                <button
                  className={`admin-unmatched-subtab${unmatchedMode === 'backlog' ? ' active' : ''}`}
                  onClick={() => setUnmatchedMode('backlog')}
                >
                  Backlog
                  {backlogItems.length > 0 && (
                    <span className="admin-alias-mode-badge">{backlogItems.length}</span>
                  )}
                </button>
              </div>

              {/* ── Queue ── */}
              {unmatchedMode === 'queue' && (
                <>
              <p className="admin-aliases-section-sub" style={{ padding: '10px 16px 4px' }}>
                Receipt text that couldn't be matched. Ignore junk lines or map them to a product.
              </p>

              {unmatchedLoading ? (
                <p className="admin-empty" style={{ padding: '24px 16px' }}>Loading…</p>
              ) : unmatchedItems.length === 0 ? (
                <div className="admin-empty" style={{ padding: '32px 16px' }}>
                  <p className="admin-empty-title">No unmatched items</p>
                  <p className="admin-empty-sub">All recent receipt lines were matched or already ignored.</p>
                </div>
              ) : (
                <div className="admin-aliases-list" style={{ marginBottom: 24 }}>
                  {unmatchedItems.filter(item => {
                    const key = `${item.productText}|${item.storeId || ''}`;
                    return !unmatchedActions[key]; // hide ignored/matched/backlogged items
                  }).map(item => {
                    const key    = `${item.productText}|${item.storeId || ''}`;
                    const action = unmatchedActions[key];
                    const isMatchingThis = matchingItem &&
                      matchingItem.productText === item.productText &&
                      matchingItem.storeId === item.storeId;

                    return (
                      <div key={key} className="admin-alias-row">
                        <div className="admin-alias-main">
                          <span className="admin-alias-text">{item.productText}</span>
                          {item.count > 1 && (
                            <span className="admin-unmatched-count">×{item.count}</span>
                          )}
                          {item.gptCleanedText && item.gptCleanedText !== item.productText && (
                            <span className="admin-alias-gpt-hint">GPT: "{item.gptCleanedText}"</span>
                          )}
                          {item.storeId && <span className="admin-alias-store">{item.storeId.replace('store_', '')}</span>}
                        </div>


                        {!action && (
                          <div className="admin-alias-unmatched-actions">
                            <button
                              className="admin-alias-ignore-btn"
                              onClick={() => handleIgnoreUnmatched(item)}
                            >
                              Ignore
                            </button>
                            <button
                              className="admin-alias-backlog-btn"
                              onClick={() => handleBacklogUnmatched(item)}
                            >
                              Later
                            </button>
                            <button
                              className="admin-alias-match-btn"
                              onClick={() => {
                                setMatchingItem(isMatchingThis ? null : item);
                                setMatchSearch('');
                                setMatchResults([]);
                              }}
                            >
                              {isMatchingThis ? 'Cancel' : 'Add alias'}
                            </button>
                          </div>
                        )}

                        {isMatchingThis && !action && (
                          <div className="admin-alias-match-panel">
                            {/* ── Assign to existing product ── */}
                            <input
                              className="admin-aliases-search"
                              type="text"
                              placeholder="Search products…"
                              value={matchSearch}
                              onChange={e => handleMatchSearch(e.target.value)}
                              autoFocus
                            />
                            {matchSearchLoading && <p className="admin-alias-match-loading">Searching…</p>}
                            {matchResults.length > 0 && (
                              <div className="admin-alias-match-results">
                                {matchResults.map(p => (
                                  <button
                                    key={p.id}
                                    className="admin-alias-match-result"
                                    onClick={() => handleSaveMatch(item, p)}
                                  >
                                    <span className="admin-alias-match-name">{p.name}</span>
                                    <span className="admin-alias-match-id">{p.id}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {/* ── Create new product ── */}
                            {!(createPanel && createPanel.item.productText === item.productText && createPanel.sourceType === 'unmatched') && (
                              <>
                                <div className="admin-create-divider"><span>or</span></div>
                                <button
                                  className="admin-create-open-btn"
                                  onClick={() => handleOpenCreate(item, 'unmatched')}
                                >
                                  + Create new product
                                </button>
                              </>
                            )}
                            {createPanel && createPanel.item.productText === item.productText && createPanel.sourceType === 'unmatched' && (
                              <div className="admin-create-product-form">
                                <div className="admin-create-form-header">
                                  <span className="admin-create-form-title">New product</span>
                                  <button className="admin-create-back" onClick={() => setCreatePanel(null)}>✕</button>
                                </div>
                                {findSimilarProducts(createPanel.form.name, allProducts).length > 0 && (
                                  <div className="admin-create-similar">
                                    <span className="admin-create-similar-label">⚠ Similar products exist — use one of these instead?</span>
                                    {findSimilarProducts(createPanel.form.name, allProducts).map(p => (
                                      <button key={p.id} className="admin-create-similar-item" onClick={() => handleSaveMatch(item, p)}>
                                        {p.name} <span className="admin-alias-match-id">{p.id}</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                                <div className="admin-create-fields">
                                  <label className="admin-create-label">
                                    Name <span className="admin-create-hint">Generic — no store, no brand, no size</span>
                                    <input className="admin-create-input" value={createPanel.form.name} placeholder="e.g. Spreadable Butter" onChange={e => handleCreateFormChange('name', e.target.value)} autoFocus />
                                  </label>
                                  <label className="admin-create-label">Category
                                    <select className="admin-create-input" value={createPanel.form.category} onChange={e => handleCreateFormChange('category', e.target.value)}>
                                      <option value="">— none —</option>
                                      {ALL_CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                    </select>
                                  </label>
                                  <label className="admin-create-label">Subcategory
                                    <select className="admin-create-input" value={createPanel.form.subcategory} onChange={e => handleCreateFormChange('subcategory', e.target.value)}>
                                      <option value="">— none —</option>
                                      {ALL_SUBCATEGORIES.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                    </select>
                                  </label>
                                  <label className="admin-create-label">
                                    Type <span className="admin-create-hint">e.g. spreadable · salted · unsalted · semi_skimmed</span>
                                    <input className="admin-create-input" value={createPanel.form.variant} onChange={e => handleCreateFormChange('variant', e.target.value)} placeholder="leave blank if none" />
                                  </label>
                                  <label className="admin-create-label">
                                    Storage type <span className="admin-create-hint">chilled (fridge) or ambient (shelf)</span>
                                    <select className="admin-create-input" value={createPanel.form.storageType} onChange={e => handleCreateFormChange('storageType', e.target.value)}>
                                      <option value="">— not applicable —</option>
                                      <option value="chilled">chilled</option>
                                      <option value="ambient">ambient</option>
                                      <option value="frozen">frozen</option>
                                    </select>
                                  </label>
                                  <div className="admin-create-label">
                                    Brand
                                    <div className="admin-create-brand-toggle">
                                      <button
                                        type="button"
                                        className={`admin-create-brand-opt${createPanel.form.ownBrand ? '' : ' active'}`}
                                        onClick={() => { handleCreateFormChange('ownBrand', false); handleCreateFormChange('brand', ''); }}
                                      >Branded</button>
                                      <button
                                        type="button"
                                        className={`admin-create-brand-opt${createPanel.form.ownBrand ? ' active' : ''}`}
                                        onClick={() => { handleCreateFormChange('ownBrand', true); handleCreateFormChange('brand', ''); }}
                                      >Own brand</button>
                                    </div>
                                    {!createPanel.form.ownBrand && (
                                      <input
                                        className="admin-create-input"
                                        value={createPanel.form.brand}
                                        onChange={e => handleCreateFormChange('brand', e.target.value)}
                                        placeholder="e.g. Oatly, Heinz, Lurpak"
                                      />
                                    )}
                                  </div>
                                  <label className="admin-create-label">
                                    Product ID <span className="admin-create-hint">auto-generated — edit if needed</span>
                                    <input className="admin-create-input admin-create-input--id" value={createPanel.form.productId} onChange={e => handleCreateFormChange('productId', e.target.value)} spellCheck={false} />
                                  </label>
                                </div>
                                {createError && <p className="admin-create-error">{createError}</p>}
                                <button className="admin-create-btn" onClick={handleCreateAndLink} disabled={createSaving}>
                                  {createSaving ? 'Creating…' : 'Create & link alias'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
                </>
              )}

              {/* ── Backlog ── */}
              {unmatchedMode === 'backlog' && (
                <>
                  <p className="admin-aliases-section-sub" style={{ padding: '10px 16px 4px' }}>
                    Real products you'll add later. Move back to queue when ready.
                  </p>
                  {backlogLoading ? (
                    <p className="admin-empty" style={{ padding: '24px 16px' }}>Loading…</p>
                  ) : backlogItems.length === 0 ? (
                    <div className="admin-empty" style={{ padding: '32px 16px' }}>
                      <p className="admin-empty-title">Backlog is empty</p>
                      <p className="admin-empty-sub">Items you mark "Later" will appear here.</p>
                    </div>
                  ) : (
                    <div className="admin-aliases-list" style={{ marginBottom: 24 }}>
                      {backlogItems.map(group => {
                        const action = backlogActions[group.productText];
                        return (
                          <div key={group.productText} className="admin-alias-row">
                            <div className="admin-alias-main">
                              <span className="admin-alias-text">{group.productText}</span>
                              {group.totalCount > 1 && (
                                <span className="admin-unmatched-count">×{group.totalCount}</span>
                              )}
                              {group.stores.filter(s => s.storeId).map(s => (
                                <span key={s.storeId} className="admin-alias-store">{s.storeId.replace('store_', '')}</span>
                              ))}
                            </div>
                            {action === 'moved'   && <p className="admin-alias-done-note">↩ Back in queue</p>}
                            {action === 'ignored' && <p className="admin-alias-done-note">Ignored</p>}
                            {!action && (
                              <div className="admin-alias-unmatched-actions">
                                <button
                                  className="admin-alias-ignore-btn"
                                  onClick={() => handleIgnoreBacklog(group)}
                                  disabled={action === 'ignoring'}
                                >
                                  {action === 'ignoring' ? '…' : 'Ignore'}
                                </button>
                                <button
                                  className="admin-unmatched-queue-btn"
                                  onClick={() => handleMoveBacklogToQueue(group)}
                                  disabled={action === 'moving'}
                                >
                                  {action === 'moving' ? '…' : '↩ Queue'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* ════════════════════════════════════════════════════════
              ALIASES MODE — browse and manage all saved rules
          ════════════════════════════════════════════════════════ */}
          {aliasMode === 'aliases' && (
            <>
              {/* Source filter pills */}
              <div className="admin-aliases-filters">
                {[
                  { val: 'all',              label: 'All' },
                  { val: 'seeded',           label: 'Seeded' },
                  { val: 'gpt',              label: 'GPT' },
                  { val: 'human_verified',   label: 'Verified' },
                  { val: 'user_correction',  label: 'Corrected' },
                  { val: 'manual',            label: 'Manual' },
                  { val: 'manual_creation',  label: 'Created' },
                  { val: 'ignore',            label: 'Ignored' },
                ].map(({ val, label }) => (
                  <button
                    key={val}
                    className={`admin-aliases-filter-btn${aliasSourceFilter === val ? ' active' : ''}`}
                    onClick={() => setAliasSourceFilter(val)}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="admin-aliases-header">
                <input
                  className="admin-aliases-search"
                  type="text"
                  placeholder="Search aliases…"
                  value={aliasSearch}
                  onChange={e => setAliasSearch(e.target.value)}
                />
                <span className="admin-aliases-count">{aliases.length} total</span>
              </div>

              {aliasesLoading ? (
                <p className="admin-empty">Loading…</p>
              ) : (
                <div className="admin-aliases-list">
                  {aliases
                    .filter(a => {
                      const src = a.source || deriveSource(a.confirmedByUid);
                      if (aliasSourceFilter !== 'all' && src !== aliasSourceFilter) return false;
                      if (!aliasSearch) return true;
                      return (
                        a.alias?.includes(aliasSearch.toLowerCase()) ||
                        a.productName?.toLowerCase().includes(aliasSearch.toLowerCase())
                      );
                    })
                    .map(a => {
                      const src = a.source || deriveSource(a.confirmedByUid);
                      return (
                        <div key={a.id} className="admin-alias-row">
                          <div className="admin-alias-main">
                            <span className="admin-alias-text">{a.alias}</span>
                            <span className="admin-alias-arrow">→</span>
                            <span className="admin-alias-product">
                              {a.productId === '__ignore__'
                                ? <em style={{ color: '#9ca3af' }}>ignored</em>
                                : (a.productName || a.productId)}
                            </span>
                          </div>
                          <div className="admin-alias-meta">
                            <span className={`admin-alias-source admin-alias-source--${src.replace(/_/g, '-')}`}>
                              {src}
                            </span>
                            {a.storeId && <span className="admin-alias-store">{a.storeId.replace('store_', '')}</span>}
                            <button
                              className="admin-alias-delete"
                              onClick={() => handleDeleteAlias(a.id)}
                              disabled={deletingAlias === a.id}
                            >
                              {deletingAlias === a.id ? '…' : 'Delete'}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  }
                  {aliases.length === 0 && !aliasesLoading && (
                    <p className="admin-empty">No aliases yet.</p>
                  )}
                </div>
              )}
            </>
          )}

        </div>
      )}

      {/* ── Requests tab ── */}
      {adminTab === 'requests' && <ProductRequests />}

      {/* ── Products tab ── */}
      {adminTab === 'products' && (
        <div className="admin-products">

          {/* Search */}
          <div className="admin-products-search-wrap">
            <input
              className="admin-products-search"
              type="text"
              placeholder="Filter by name, category or ID…"
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
            />
            {productSearch && (
              <button className="admin-products-clear" onClick={() => setProductSearch('')} aria-label="Clear">✕</button>
            )}
          </div>

          {productsLoading ? (
            <div className="admin-loading">Loading products…</div>
          ) : (() => {
            const term = productSearch.toLowerCase().trim();
            const visible = term
              ? products.filter(p =>
                  (p.name || '').toLowerCase().includes(term) ||
                  (p.category || '').toLowerCase().includes(term) ||
                  (p.id || '').toLowerCase().includes(term)
                )
              : products;

            return visible.length === 0 ? (
              <div className="admin-empty">
                <p className="admin-empty-title">No products found</p>
                <p className="admin-empty-sub">Try a different search term.</p>
              </div>
            ) : (
              <>
                <p className="admin-products-count">{visible.length} of {products.length} product{products.length !== 1 ? 's' : ''}</p>
                <div className="admin-products-list">
                  {visible.map(p => {
                    const isExpanded = expandedProduct === p.id;
                    const retailers  = p.retailers || [];

                    // Derive a display size: prefer product-level field, fall back to first retailer that has one
                    const displaySize = p.standardizedSize
                      ? `${p.standardizedSize}${p.unitType ? ' ' + p.unitType : ''}`
                      : (retailers.find(r => r.size)?.size || null);

                    // Build retailer pill summary e.g. "Aldi • Tesco"
                    const retailerNames = retailers.map(r =>
                      STORE_NAMES[(r.retailerId || r.id || '').replace(/^store_/, '')] || r.retailerId || r.id
                    );

                    return (
                      <div key={p.id} className={`admin-product-card${isExpanded ? ' expanded' : ''}`}>

                        {/* ── Collapsed header (always visible) ── */}
                        <button
                          className="admin-product-header"
                          onClick={() => {
                            setExpandedProduct(isExpanded ? null : p.id);
                            if (!isExpanded) setConfirmDelete(null); // reset any pending delete
                          }}
                          aria-expanded={isExpanded}
                        >
                          <div className="admin-product-header-left">
                            <span className="admin-product-name">{p.name || p.id}</span>
                            <span className="admin-product-header-meta">
                              {p.category && <span className="admin-product-tag">{p.category.replace(/_/g, ' ')}</span>}
                              {p.tier     && <span className="admin-product-tag admin-product-tag--tier">{p.tier}</span>}
                              {displaySize && <span className="admin-product-tag admin-product-tag--size">{displaySize}</span>}
                            </span>
                            {retailerNames.length > 0 && (
                              <span className="admin-product-retailers-summary">
                                {retailerNames.join(' • ')}
                                <span className="admin-product-retailer-count">({retailers.length})</span>
                              </span>
                            )}
                          </div>
                          <span className="admin-product-chevron" aria-hidden="true">
                            {isExpanded ? '▲' : '▼'}
                          </span>
                        </button>

                        {/* ── Expanded body ── */}
                        {isExpanded && (
                          <div className="admin-product-body">
                            {/* Detail rows */}
                            <div className="admin-product-detail-grid">
                              {p.tier        && <><span className="apd-key">Tier</span>     <span className="apd-val">{p.tier}</span></>}
                              {p.category    && <><span className="apd-key">Category</span> <span className="apd-val">{p.category.replace(/_/g, ' ')}</span></>}
                              {p.subcategory && <><span className="apd-key">Subcategory</span><span className="apd-val">{p.subcategory}</span></>}
                              {displaySize   && <><span className="apd-key">Size</span>     <span className="apd-val">{displaySize}</span></>}
                              {p.brand       && <><span className="apd-key">Brand</span>    <span className="apd-val">{p.brand}</span></>}
                            </div>

                            {/* Retailer rows */}
                            {retailers.length > 0 && (
                              <div className="admin-product-retailer-section">
                                <span className="admin-product-retailer-heading">Retailers</span>
                                {retailers.map(r => {
                                  const rName  = STORE_NAMES[(r.retailerId || r.id || '').replace(/^store_/, '')] || r.retailerId || r.id;
                                  const price  = r.latestPricePence ? `£${(r.latestPricePence / 100).toFixed(2)}` : null;
                                  const rSize  = r.size || null;
                                  const inStock = r.inStock !== false; // default true if unknown
                                  return (
                                    <div key={r.id} className="admin-product-retailer-row">
                                      <span className="apr-name">{rName}</span>
                                      {price  && <span className="apr-price">{price}</span>}
                                      {rSize  && <span className="apr-size">{rSize}</span>}
                                      <span className={`apr-stock${inStock ? ' in' : ' out'}`}>{inStock ? 'In stock' : 'Out'}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Product ID */}
                            <div className="admin-product-id-row">
                              <span className="admin-product-id-label">Product ID</span>
                              <span className="admin-product-id">{p.id}</span>
                            </div>

                            {/* Delete controls */}
                            <div className="admin-product-actions">
                              {confirmDelete === p.id ? (
                                <>
                                  <span className="admin-product-confirm-label">Permanently delete this product and all retailer data?</span>
                                  <div className="admin-product-action-btns">
                                    <button
                                      className="admin-product-btn admin-product-btn--confirm"
                                      onClick={() => handleDeleteProduct(p.id)}
                                      disabled={deleting === p.id}
                                    >
                                      {deleting === p.id ? 'Deleting…' : 'Yes, delete'}
                                    </button>
                                    <button
                                      className="admin-product-btn admin-product-btn--cancel"
                                      onClick={() => setConfirmDelete(null)}
                                      disabled={deleting === p.id}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <button
                                  className="admin-product-btn admin-product-btn--delete"
                                  onClick={() => setConfirmDelete(p.id)}
                                >
                                  Delete product
                                </button>
                              )}
                            </div>
                          </div>
                        )}

                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* ── Price Reviews tab ── */}
      {adminTab === 'reviews' && (
        <div className="admin-reviews">

          {/* ── Fetch run summary tile ── */}
          {lastRun && (
            <div className={`admin-run-tile${lastRun.alertFlag ? ' alert' : ''}`}>
              <span className="admin-run-label">Last fetch run</span>
              <div className="admin-run-stats">
                <span>{lastRun.successCount ?? 0} fetched</span>
                <span>{lastRun.pendingCreated ?? 0} new reviews</span>
                {lastRun.parseFailedCount > 0 && (
                  <span className="admin-run-warn">{lastRun.parseFailedCount} parse failed</span>
                )}
                {lastRun.alertFlag && (
                  <span className="admin-run-alert">⚠ High failure rate</span>
                )}
              </div>
              <span className="admin-run-time">{timeAgo(lastRun.finishedAt ?? lastRun.startedAt)}</span>
            </div>
          )}

          {/* ── Summary pills ── */}
          <div className="admin-summary">
            <div className="admin-summary-pill pending-pill">
              <span className="admin-summary-count">{pendingReviews.length}</span>
              <span className="admin-summary-label">Pending</span>
            </div>
            <div className="admin-summary-pill actioned-pill">
              <span className="admin-summary-count">{reviews.filter(r => r.status === 'approved').length}</span>
              <span className="admin-summary-label">Approved</span>
            </div>
            <div className="admin-summary-pill total-pill">
              <span className="admin-summary-count">{reviews.length}</span>
              <span className="admin-summary-label">Total</span>
            </div>
          </div>

          {/* ── Batch approve ── */}
          {batchEligible.length > 0 && (
            <div className="admin-batch-bar">
              {batchConfirm ? (
                <>
                  <span className="admin-batch-confirm-label">
                    Approve {batchEligible.length} price update{batchEligible.length !== 1 ? 's' : ''}?
                  </span>
                  <button className="admin-batch-btn approve" onClick={handleBatchApprove} disabled={batching}>
                    {batching ? 'Approving…' : 'Confirm'}
                  </button>
                  <button className="admin-batch-btn cancel" onClick={() => setBatchConfirm(false)} disabled={batching}>
                    Cancel
                  </button>
                </>
              ) : (
                <button className="admin-batch-btn approve" onClick={() => setBatchConfirm(true)}>
                  Approve {batchEligible.length} eligible
                </button>
              )}
              <span className="admin-batch-hint">No promo · change ≤{Math.round(threshold * 100)}%</span>
            </div>
          )}

          {/* ── Filter bar ── */}
          <div className="admin-filter-tabs">
            {[
              { key: 'pending',   label: 'Pending' },
              { key: 'approved',  label: 'Approved' },
              { key: 'rejected',  label: 'Rejected' },
              { key: 'community', label: 'Community' },
              { key: 'all',       label: 'All' },
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`admin-filter-tab${reviewFilter === key ? ' active' : ''}`}
                onClick={() => setReviewFilter(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {/* ── Review list ── */}
          {reviewsLoading ? (
            <div className="admin-loading">Loading reviews…</div>
          ) : visibleReviews.length === 0 ? (
            <div className="admin-empty">
              <p className="admin-empty-title">
                {reviewFilter === 'pending' ? 'No pending reviews' : 'Nothing here'}
              </p>
              <p className="admin-empty-sub">
                {reviewFilter === 'pending'
                  ? 'Price changes from the fetcher, receipts, and community will appear here.'
                  : `No ${reviewFilter} reviews at the moment.`}
              </p>
            </div>
          ) : (
            <div className="admin-flags-list">
              {visibleReviews.map(review => {
                const diff       = diffLabel(review.proposedPricePence, review.currentStoredPricePence);
                const isActioning = actioning.has(review.id);
                const isPending  = review.status === 'pending_review';
                const noPrice    = review.proposedPricePence == null;

                return (
                  <div
                    key={review.id}
                    className={`admin-flag-card${!isPending ? ' actioned' : ''}${noPrice ? ' no-price' : ''}`}
                  >
                    {/* Top row: product + source badge */}
                    <div className="admin-flag-top">
                      <div className="admin-flag-product">
                        {review.productName || review.productId || <span className="admin-flag-unknown">Unknown product</span>}
                      </div>
                      <div className="admin-review-badges">
                        {review.hasPromo && (
                          <span className="admin-review-badge promo">Promo</span>
                        )}
                        <span className={`admin-review-badge source-${review.sourceType?.replace('_', '-') ?? 'unknown'}`}>
                          {SOURCE_LABELS[review.sourceType] ?? review.sourceType}
                        </span>
                        <span className={`admin-flag-badge badge-${review.status?.replace('_review', '') ?? 'pending'}`}>
                          {review.status === 'pending_review' ? 'Pending' : review.status}
                        </span>
                      </div>
                    </div>

                    {/* Receipt context — only for receipt_ocr source */}
                    {review.sourceType === 'receipt_ocr' && review.receiptProductText && (
                      <div className="admin-review-receipt-context">
                        <span className="admin-review-receipt-label">On receipt:</span>
                        <span className="admin-review-receipt-text">"{review.receiptProductText}"</span>
                        {review.confidence && (
                          <span className={`admin-review-confidence conf-${review.confidence}`}>
                            {review.confidence} match
                          </span>
                        )}
                      </div>
                    )}

                    {/* Price row */}
                    <div className="admin-review-prices">
                      <span className="admin-review-store">{storeName(review.storeId)}</span>
                      {noPrice ? (
                        <span className="admin-review-no-price">No price provided — verify manually</span>
                      ) : (
                        <>
                          <span className="admin-review-current">{formatPence(review.currentStoredPricePence)}</span>
                          <span className="admin-review-arrow">→</span>
                          <span className="admin-review-proposed">{formatPence(review.proposedPricePence)}</span>
                          {diff && (
                            <span className={`admin-review-diff ${diff.up ? 'up' : 'down'}`}>{diff.str}</span>
                          )}
                        </>
                      )}
                    </div>

                    {/* Footer: time + actions */}
                    <div className="admin-flag-footer">
                      <span className="admin-flag-date">{timeAgo(review.fetchedAt)}</span>
                      {isPending && (
                        <div className="admin-review-actions">
                          {noPrice ? (
                            <button
                              className="admin-action-btn action-btn"
                              onClick={() => handleReject(review)}
                              disabled={isActioning}
                            >
                              {isActioning ? '…' : 'Dismiss'}
                            </button>
                          ) : (
                            <>
                              <button
                                className="admin-action-btn approve-btn"
                                onClick={() => handleApprove(review)}
                                disabled={isActioning}
                              >
                                {isActioning ? '…' : 'Approve'}
                              </button>
                              <button
                                className="admin-action-btn reject-btn"
                                onClick={() => handleReject(review)}
                                disabled={isActioning}
                              >
                                Reject
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                  </div>
                );
              })}
            </div>
          )}

        </div>
      )}

      {/* ── Add-to-product modal ── */}
      {addProductModal && (
        <div className="ap-overlay" onClick={() => setAddProductModal(null)}>
          <div className="ap-modal" onClick={e => e.stopPropagation()}>
            <div className="ap-modal-header">
              <h3 className="ap-modal-title">Add to products</h3>
              <button className="ap-modal-close" onClick={() => setAddProductModal(null)}>✕</button>
            </div>

            <p className="ap-receipt-line">
              <span className="ap-receipt-label">From receipt:</span>
              <span className="ap-receipt-value">"{addProductModal.receiptText}"</span>
            </p>

            <div className="ap-fields">
              <label className="ap-field">
                <span className="ap-field-label">Product name</span>
                <input
                  className="ap-input"
                  value={apForm.productName}
                  onChange={e => handleApChange('productName', e.target.value)}
                  placeholder="e.g. Fred & Flo Extra Sensitive Wipes"
                />
              </label>

              <div className="ap-field-row">
                <label className="ap-field">
                  <span className="ap-field-label">Brand</span>
                  <input
                    className="ap-input"
                    value={apForm.brand}
                    onChange={e => handleApChange('brand', e.target.value)}
                    placeholder="e.g. Fred & Flo"
                  />
                </label>
                <label className="ap-field">
                  <span className="ap-field-label">Size</span>
                  <input
                    className="ap-input"
                    value={apForm.size}
                    onChange={e => handleApChange('size', e.target.value)}
                    placeholder="e.g. 500ml"
                  />
                </label>
              </div>

              <div className="ap-field-row">
                <label className="ap-field">
                  <span className="ap-field-label">Category</span>
                  <select className="ap-select" value={apForm.category} onChange={e => handleApChange('category', e.target.value)}>
                    <option value="">— choose —</option>
                    {ALL_CATEGORIES.map(c => (
                      <option key={c.id} value={c.id}>{c.aisle} › {c.id.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </label>
                <label className="ap-field">
                  <span className="ap-field-label">Subcategory</span>
                  <select className="ap-select" value={apForm.subcategory} onChange={e => handleApChange('subcategory', e.target.value)}>
                    <option value="">— choose —</option>
                    {ALL_SUBCATEGORIES.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="ap-field-row">
                <label className="ap-field">
                  <span className="ap-field-label">Store</span>
                  <select className="ap-select" value={apForm.store} onChange={e => handleApChange('store', e.target.value)}>
                    <option value="">— choose —</option>
                    {Object.values(STORE_DISPLAY_NAMES).map(s => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </label>
                <label className="ap-field">
                  <span className="ap-field-label">Price (£)</span>
                  <input
                    className="ap-input"
                    value={apForm.price}
                    onChange={e => handleApChange('price', e.target.value)}
                    placeholder="e.g. 2.50"
                    type="number"
                    step="0.01"
                    min="0"
                  />
                </label>
              </div>

              <label className="ap-field">
                <span className="ap-field-label">Variant <span className="ap-field-hint">(optional)</span></span>
                <input
                  className="ap-input"
                  value={apForm.variant}
                  onChange={e => handleApChange('variant', e.target.value)}
                  placeholder="e.g. sensitive, mature, low fat"
                />
              </label>
            </div>

            <div className="ap-modal-footer">
              <p className="ap-instructions">
                Copy the row below, then paste it into your <strong>products_master</strong> Google Sheet. Run the sync script when done.
              </p>
              <button
                className={`ap-copy-btn${apCopied ? ' copied' : ''}`}
                onClick={handleCopyRow}
                disabled={!apForm.productName || !apForm.category}
              >
                {apCopied ? '✓ Copied!' : '📋 Copy sheet row'}
              </button>
              {(!apForm.productName || !apForm.category) && (
                <p className="ap-validation-hint">Product name and category required before copying</p>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdminPage;
